import React, { useState, useEffect, useMemo, Fragment } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { Dialog, Transition, Popover } from "@headlessui/react";
import {
  HiOutlineUsers,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineMagnifyingGlass,
  HiOutlineFunnel,
  HiOutlineEye,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineXMark,
  HiChevronLeft,
  HiChevronRight,
  HiChevronDoubleLeft,
  HiChevronDoubleRight,
  HiChevronDown,
  HiOutlineArrowDownTray,
  HiOutlineArrowPath,
  HiOutlineExclamationCircle,
  HiOutlineCheck,
  HiOutlineUserPlus,
  HiOutlineBuildingOffice2,
  HiOutlineMapPin,
  HiOutlineSparkles,
  HiOutlineLink,
  HiOutlineTrash,
  HiOutlineLockClosed,
  HiOutlineDocumentArrowUp,
  HiOutlineDocumentPlus,
  HiOutlineDocumentText,
  HiOutlineChatBubbleLeftRight,
  HiOutlineArrowTopRightOnSquare,
  HiOutlinePencilSquare,
  HiOutlineXCircle,
  HiOutlineShieldCheck,
  HiOutlineClipboardDocumentList
} from "react-icons/hi2";
import Sidebar from "../../partials/Sidebar";
import Header from "../../partials/Header";
import Banner from "../../partials/Banner";
import { API_URL } from "../../config";
import { logAuditUI } from "../../utils/auditLogger";

const THERAPEUTIC_AREAS_MAP = {
  cardiology: "Cardiologie",
  traumatology_orthopedics: "Traumatologie / Ortopedie",
  neurology: "Neurologie",
  endocrinology_diabetology: "Endocrinologie / Diabetologie",
  family_medicine: "Medicină de familie",
  gynecology_obstetrics: "Ginecologie / Obstetrică",
  allergology_immunology: "Alergologie / Imunologie",
  gastroenterology: "Gastroenterologie",
  pneumology: "Pneumologie",
  orl: "ORL",
  ophthalmology: "Oftalmologie",
  dermatology: "Dermatologie",
  urology: "Urologie",
  nephrology: "Nefrologie",
  rheumatology: "Reumatologie",
  pediatrics: "Pediatrie",
  psychiatry: "Psihiatrie",
  oncology: "Oncologie",
  hematology: "Hematologie",
  infectious_diseases: "Boli infecțioase",
  surgery: "Chirurgie",
  internal_medicine: "Medicină internă",
  dentistry: "Stomatologie",
  nutrition_supplements: "Nutriție / Suplimente",
  other: "Alte arii"
};

const getInitials = (firstName, lastName) => {
  const f = firstName ? firstName.trim()[0] : "";
  const l = lastName ? lastName.trim()[0] : "";
  const combined = (f + l).toUpperCase();
  return combined || "DR";
};

const parseJsonArray = (input) => {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  try {
    const parsed = JSON.parse(input);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return typeof input === "string" ? input.split(",").map((s) => s.trim()) : [];
  }
};

const normalizeText = (text) => {
  if (!text) return "";
  return text
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

const extractWords = (text) => {
  return normalizeText(text)
    .split(/[\s,.\-\/_()]+/)
    .filter((w) => w.length >= 2);
};

export default function MarketingConsent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [consents, setConsents] = useState([]);
  const [physiciansList, setPhysiciansList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPhysicians, setLoadingPhysicians] = useState(false);

  // Active Main Tab: 'all' (Toate acordurile), 'linked' (Asociați / confirmați), 'unlinked' (Neatribuite)
  const [activeTab, setActiveTab] = useState("all");

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'confirmed', 'pending'
  const [selectedChannels, setSelectedChannels] = useState([]); // ['email', 'sms', 'phone']
  const [sortBy, setSortBy] = useState("recent"); // 'recent', 'oldest', 'name_asc', 'name_desc'

  // Modal Details
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Upload Physical Document Modal State
  const INITIAL_DOC_FORM = {
    lastName: "",
    firstName: "",
    specialization: "",
    medicalUnit: "",
    phone: "",
    email: "",
    communicationChannels: ["document"],
    therapeuticAreas: [],
    otherTherapeuticArea: "",
    marketingConsent: true,
    personUniqueId: null,
  };

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadStep, setUploadStep] = useState(1); // 1: File, 2: Form
  const [docFiles, setDocFiles] = useState([]); // [{ name, size, type, base64 }]
  const [docFormData, setDocFormData] = useState(INITIAL_DOC_FORM);
  const [isSubmittingDoc, setIsSubmittingDoc] = useState(false);
  const [docFormError, setDocFormError] = useState(null);
  const [specialitiesList, setSpecialitiesList] = useState([]);

  // Edit Marketing Consent Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState(INITIAL_DOC_FORM);
  const [editDocFiles, setEditDocFiles] = useState([]); // [{ name, size, type, base64, path }]
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [editFormError, setEditFormError] = useState(null);

  // Scanned Document Viewer Modal State
  const [docViewerOpen, setDocViewerOpen] = useState(false);
  const [docViewerRecord, setDocViewerRecord] = useState(null);
  const [activeDocIndex, setActiveDocIndex] = useState(0);

  // Doctor/Pharmacist Linking Pop-up State
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [targetConsent, setTargetConsent] = useState(null);
  const [assignSearch, setAssignSearch] = useState("");
  const [selectedPersonUniqueId, setSelectedPersonUniqueId] = useState(null);
  const [isSavingLink, setIsSavingLink] = useState(false);

  // Audit Logs State for Details Modal
  const [consentAuditLogs, setConsentAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);

  // Pagination
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const token = localStorage.getItem("jwtToken");

  const canEdit = useMemo(() => {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      const role = (decoded?.role || decoded?.role_name || "").toLowerCase().trim();
      return (
        role === "admin" ||
        role === "marketing monitor" ||
        decoded?.role_id === 1 ||
        decoded?.role_id === 4
      );
    } catch {
      return false;
    }
  }, [token]);

  const fetchConsents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/marketing-consent`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setConsents(Array.isArray(res.data) ? res.data : []);
      logAuditUI({
        actionType: "VIEW_LIST",
        module: "Marketing",
        description: "A accesat lista de acorduri de date marketing",
        details: { count: Array.isArray(res.data) ? res.data.length : 0 },
      });
    } catch (err) {
      console.error("Error loading marketing consents:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignablePersons = async () => {
    setLoadingPhysicians(true);
    try {
      const res = await axios.get(`${API_URL}/api/marketing-consent/assignable-persons`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      // Deduplicate by unique_id
      const uniquePersons = [];
      const seen = new Set();
      if (Array.isArray(res.data)) {
        res.data.forEach((p) => {
          if (!seen.has(p.unique_id)) {
            seen.add(p.unique_id);
            uniquePersons.push(p);
          }
        });
      }
      setPhysiciansList(uniquePersons);
    } catch (err) {
      console.error("Error loading assignable persons:", err);
    } finally {
      setLoadingPhysicians(false);
    }
  };

  const fetchSpecialities = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/marketing-consent/specialities`);
      if (Array.isArray(res.data)) {
        setSpecialitiesList(res.data);
      }
    } catch (err) {
      console.error("Error loading specialities:", err);
    }
  };

  useEffect(() => {
    fetchConsents();
    fetchAssignablePersons();
    fetchSpecialities();
  }, []);

  const [isDragging, setIsDragging] = useState(false);

  const processSelectedFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;

    const filesArray = Array.from(fileList);
    const oversized = filesArray.filter((f) => f.size > 15 * 1024 * 1024);
    if (oversized.length > 0) {
      setDocFormError("Unele fișiere depășesc limita de 15 MB.");
      return;
    }

    setDocFormError(null);

    const promises = filesArray.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            name: file.name,
            size: file.size,
            type: file.type,
            base64: reader.result,
          });
        };
        reader.onerror = () => reject(new Error("Eroare la citirea fișierului"));
        reader.readAsDataURL(file);
      });
    });

    Promise.all(promises)
      .then((newProcessedFiles) => {
        setDocFiles((prev) => [...prev, ...newProcessedFiles]);
      })
      .catch((err) => {
        console.error(err);
        setDocFormError("Eroare la citirea fișierelor selectate.");
      });
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processSelectedFiles(files);
      e.target.value = "";
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setDocFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleResetUploadModal = () => {
    setUploadModalOpen(false);
    setUploadStep(1);
    setDocFiles([]);
    setDocFormData(INITIAL_DOC_FORM);
    setDocFormError(null);
    setIsSubmittingDoc(false);
    setIsDragging(false);
  };

  const handleDocFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "phone") {
      const sanitized = value.replace(/[^\d\s]/g, "").slice(0, 11);
      setDocFormData((prev) => ({ ...prev, phone: sanitized }));
      return;
    }
    setDocFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleDocChannel = (channel) => {
    setDocFormData((prev) => {
      const exists = prev.communicationChannels.includes(channel);
      const next = exists
        ? prev.communicationChannels.filter((c) => c !== channel)
        : [...prev.communicationChannels, channel];
      return { ...prev, communicationChannels: next };
    });
  };

  const toggleDocTherapeuticArea = (areaKey) => {
    setDocFormData((prev) => {
      const exists = prev.therapeuticAreas.includes(areaKey);
      const next = exists
        ? prev.therapeuticAreas.filter((a) => a !== areaKey)
        : [...prev.therapeuticAreas, areaKey];
      return { ...prev, therapeuticAreas: next };
    });
  };

  const toggleDocAllTherapeuticAreas = () => {
    setDocFormData((prev) => {
      const allKeys = Object.keys(THERAPEUTIC_AREAS_MAP).filter((k) => k !== "other");
      const isAll = allKeys.every((k) => prev.therapeuticAreas.includes(k));
      return {
        ...prev,
        therapeuticAreas: isAll ? [] : allKeys,
      };
    });
  };

  const getScannedDocuments = (record) => {
    if (!record?.document_path) return [];
    try {
      const paths = JSON.parse(record.document_path);
      const names = record.document_original_name ? JSON.parse(record.document_original_name) : [];
      if (Array.isArray(paths)) {
        return paths.map((p, idx) => ({
          path: p,
          name: (Array.isArray(names) && names[idx]) || `Document_${idx + 1}.pdf`
        }));
      }
    } catch (e) {}
    return [{
      path: record.document_path,
      name: record.document_original_name || "Document_acord_semnat.pdf"
    }];
  };

  const getDocumentUrl = (doc, isDownload = false) => {
    if (!doc?.path) return "";
    if (doc.path.startsWith("http://") || doc.path.startsWith("https://")) {
      return doc.path;
    }
    const cleanPath = doc.path.startsWith("/") ? doc.path : `/${doc.path}`;
    return `${API_URL}${cleanPath}`;
  };

  const handleDownloadDoc = async (doc, record = null) => {
    if (!doc?.path) return;
    const currentRec = record || docViewerRecord || selectedRecord;
    const fileName = doc.name || "Document_acord.pdf";

    logAuditUI({
      actionType: "DOWNLOAD",
      module: "Acord de date",
      entityType: "marketing_consent",
      entityId: currentRec?.consent_id || null,
      entityName: currentRec ? `${currentRec.last_name || ""} ${currentRec.first_name || ""}`.trim() : fileName,
      description: `A downloadat fișierul de acord: ${fileName}${currentRec ? ` (${currentRec.last_name || ""} ${currentRec.first_name || ""})` : ""}`,
      details: {
        consent_id: currentRec?.consent_id || null,
        file_name: fileName,
        file_path: doc.path,
      },
    });

    const url = getDocumentUrl(doc, true);
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      window.open(url, "_blank");
    }
  };

  const handleOpenDocViewer = (record, initialIndex = 0) => {
    if (!record) return;
    setDocViewerRecord(record);
    setActiveDocIndex(initialIndex);
    setDocViewerOpen(true);
    logAuditUI({
      actionType: "VIEW_DOCUMENT",
      module: "Acord de date",
      entityType: "marketing_consent",
      entityId: record.consent_id,
      entityName: `${record.last_name || ""} ${record.first_name || ""}`.trim(),
      description: `A vizualizat documentul fizic de acord pentru ${record.last_name || ""} ${record.first_name || ""}`.trim(),
      details: {
        consent_id: record.consent_id,
        doc_index: initialIndex,
      },
    });
  };

  const handleOpenDetails = async (record) => {
    if (!record) return;
    setSelectedRecord(record);
    setDetailsOpen(true);
    setConsentAuditLogs([]);
    setLoadingAuditLogs(true);

    logAuditUI({
      actionType: "VIEW_DETAILS",
      module: "Acord de date",
      entityType: "marketing_consent",
      entityId: record.consent_id,
      entityName: `${record.last_name || ""} ${record.first_name || ""}`.trim(),
      description: `A vizualizat ancheta / detaliile acordului de date pentru ${record.last_name || ""} ${record.first_name || ""}`.trim(),
      details: {
        consent_id: record.consent_id,
        specialization: record.specialization,
        medical_unit: record.medical_unit,
        is_confirmed: record.is_confirmed,
      },
    });

    try {
      const res = await axios.get(`${API_URL}/api/marketing-consent/${record.consent_id}/audit-history`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setConsentAuditLogs(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.warn("Nu s-a putut prelua istoricul de audit:", e.message);
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  const handleOpenEdit = (record) => {
    if (!record) return;
    setEditingRecord(record);
    const areas = parseJsonArray(record.therapeutic_areas);
    const channels = parseJsonArray(record.communication_channels);
    const scannedDocs = getScannedDocuments(record);

    setEditFormData({
      lastName: record.last_name || "",
      firstName: record.first_name || "",
      specialization: record.specialization || "",
      medicalUnit: record.medical_unit || "",
      phone: record.phone || "",
      email: record.email || "",
      communicationChannels: channels.length > 0 ? channels : ["document"],
      therapeuticAreas: areas,
      otherTherapeuticArea: record.other_therapeutic_area || "",
    });

    setEditDocFiles(scannedDocs.map((d) => ({
      name: d.name,
      path: d.path,
    })));

    setEditFormError(null);
    setEditModalOpen(true);
  };

  const handleEditFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "phone") {
      const sanitized = value.replace(/[^\d\s]/g, "").slice(0, 11);
      setEditFormData((prev) => ({ ...prev, phone: sanitized }));
      return;
    }
    setEditFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleEditChannel = (channel) => {
    setEditFormData((prev) => {
      const exists = prev.communicationChannels.includes(channel);
      const next = exists
        ? prev.communicationChannels.filter((c) => c !== channel)
        : [...prev.communicationChannels, channel];
      return { ...prev, communicationChannels: next };
    });
  };

  const toggleEditTherapeuticArea = (areaKey) => {
    setEditFormData((prev) => {
      const exists = prev.therapeuticAreas.includes(areaKey);
      const next = exists
        ? prev.therapeuticAreas.filter((a) => a !== areaKey)
        : [...prev.therapeuticAreas, areaKey];
      return { ...prev, therapeuticAreas: next };
    });
  };

  const toggleEditAllTherapeuticAreas = () => {
    setEditFormData((prev) => {
      const allKeys = Object.keys(THERAPEUTIC_AREAS_MAP).filter((k) => k !== "other");
      const isAll = allKeys.every((k) => prev.therapeuticAreas.includes(k));
      return {
        ...prev,
        therapeuticAreas: isAll ? [] : allKeys,
      };
    });
  };

  const processEditSelectedFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const filesArray = Array.from(fileList);
    const oversized = filesArray.filter((f) => f.size > 15 * 1024 * 1024);
    if (oversized.length > 0) {
      setEditFormError("Unele fișiere depășesc limita de 15 MB.");
      return;
    }

    setEditFormError(null);
    const promises = filesArray.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            name: file.name,
            size: file.size,
            type: file.type,
            base64: reader.result,
          });
        };
        reader.onerror = () => reject(new Error("Eroare la citirea fișierului"));
        reader.readAsDataURL(file);
      });
    });

    Promise.all(promises)
      .then((newProcessedFiles) => {
        setEditDocFiles((prev) => [...prev, ...newProcessedFiles]);
      })
      .catch((err) => {
        console.error(err);
        setEditFormError("Eroare la citirea fișierelor selectate.");
      });
  };

  const handleEditFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processEditSelectedFiles(files);
      e.target.value = "";
    }
  };

  const handleRemoveEditFile = (indexToRemove) => {
    setEditDocFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleSubmitEdit = async () => {
    if (!editingRecord) return;
    setEditFormError(null);

    if (!editFormData.lastName.trim()) {
      setEditFormError("Introduceți numele de familie.");
      return;
    }
    if (!editFormData.firstName.trim()) {
      setEditFormError("Introduceți prenumele.");
      return;
    }
    if (!editFormData.specialization.trim()) {
      setEditFormError("Selectați sau introduceți specializarea.");
      return;
    }
    if (!editFormData.medicalUnit.trim()) {
      setEditFormError("Introduceți unitatea medicală sau farmacia.");
      return;
    }
    if (!editFormData.email.trim()) {
      setEditFormError("Introduceți o adresă de email validă.");
      return;
    }
    if (editFormData.therapeuticAreas.length === 0) {
      setEditFormError("Selectați cel puțin o arie terapeutică.");
      return;
    }

    setIsSubmittingEdit(true);
    try {
      const payload = {
        lastName: editFormData.lastName.trim(),
        firstName: editFormData.firstName.trim(),
        specialization: editFormData.specialization.trim(),
        medicalUnit: editFormData.medicalUnit.trim(),
        phone: editFormData.phone ? editFormData.phone.trim() : null,
        email: editFormData.email.trim().toLowerCase(),
        therapeuticAreas: editFormData.therapeuticAreas,
        otherTherapeuticArea: editFormData.otherTherapeuticArea ? editFormData.otherTherapeuticArea.trim() : null,
        communicationChannels: editFormData.communicationChannels,
        files: editDocFiles.map((f) => ({
          name: f.name,
          path: f.path || null,
          base64: f.base64 || null,
        })),
      };

      const res = await axios.put(`${API_URL}/api/marketing-consent/${editingRecord.consent_id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.data?.success) {
        logAuditUI({
          actionType: "UPDATE",
          module: "Acord de date",
          entityType: "marketing_consent",
          entityId: editingRecord.consent_id,
          entityName: `${editFormData.lastName.trim()} ${editFormData.firstName.trim()}`,
          description: `A salvat modificările pentru acordul de date al lui ${editFormData.lastName.trim()} ${editFormData.firstName.trim()}`,
          details: {
            consent_id: editingRecord.consent_id,
            old_name: `${editingRecord.last_name || ""} ${editingRecord.first_name || ""}`.trim(),
            new_name: `${editFormData.lastName.trim()} ${editFormData.firstName.trim()}`,
            specialization: editFormData.specialization.trim(),
            medicalUnit: editFormData.medicalUnit.trim(),
          },
        });

        // Optimistic UI update
        setConsents((prev) =>
          prev.map((item) =>
            item.consent_id === editingRecord.consent_id
              ? {
                  ...item,
                  last_name: editFormData.lastName.trim(),
                  first_name: editFormData.firstName.trim(),
                  specialization: editFormData.specialization.trim(),
                  medical_unit: editFormData.medicalUnit.trim(),
                  phone: editFormData.phone ? editFormData.phone.trim() : null,
                  email: editFormData.email.trim().toLowerCase(),
                  therapeutic_areas: JSON.stringify(editFormData.therapeuticAreas),
                  other_therapeutic_area: editFormData.otherTherapeuticArea ? editFormData.otherTherapeuticArea.trim() : null,
                  communication_channels: JSON.stringify(editFormData.communicationChannels),
                }
              : item
          )
        );

        setEditModalOpen(false);
        setEditingRecord(null);
        await fetchConsents();
      }
    } catch (err) {
      console.error("Error submitting edit:", err);
      setEditFormError(err.response?.data?.error || "Eroare la salvarea modificărilor.");
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleSubmitManualDocument = async () => {
    setDocFormError(null);

    // Validation
    if (!docFormData.lastName.trim()) {
      setDocFormError("Introduceți numele de familie.");
      return;
    }
    if (!docFormData.firstName.trim()) {
      setDocFormError("Introduceți prenumele.");
      return;
    }
    if (!docFormData.specialization.trim()) {
      setDocFormError("Selectați sau introduceți specializarea.");
      return;
    }
    if (!docFormData.medicalUnit.trim()) {
      setDocFormError("Introduceți unitatea medicală sau farmacia.");
      return;
    }
    if (!docFormData.email.trim()) {
      setDocFormError("Introduceți o adresă de email validă.");
      return;
    }
    if (docFormData.therapeuticAreas.length === 0) {
      setDocFormError("Selectați cel puțin o arie terapeutică.");
      return;
    }

    setIsSubmittingDoc(true);
    try {
      const selectedPerson = physiciansList.find((p) => p.unique_id === docFormData.personUniqueId);
      const physician_id = selectedPerson?.person_type === "physician" ? selectedPerson.physician_id : null;
      const pharmacist_id = selectedPerson?.person_type === "pharmacist" ? selectedPerson.pharmacist_id : null;

      const payload = {
        ...docFormData,
        physician_id,
        pharmacist_id,
        files: docFiles.map((f) => ({
          name: f.name,
          size: f.size,
          type: f.type,
          base64: f.base64,
        })),
      };

      const res = await axios.post(`${API_URL}/api/marketing-consent/manual-document`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.data?.success) {
        logAuditUI({
          actionType: "CREATE",
          module: "Acord de date",
          entityType: "marketing_consent",
          entityId: res.data?.consent_id || null,
          entityName: `${docFormData.lastName.trim()} ${docFormData.firstName.trim()}`,
          description: `A adăugat acord de date prin document fizic pentru ${docFormData.lastName.trim()} ${docFormData.firstName.trim()} (${docFormData.specialization.trim()})`,
          details: {
            consent_id: res.data?.consent_id,
            email: docFormData.email,
            phone: docFormData.phone,
            medicalUnit: docFormData.medicalUnit,
            filesCount: docFiles.length,
          },
        });
        handleResetUploadModal();
        await fetchConsents();
      }
    } catch (err) {
      console.error("Error submitting manual document:", err);
      setDocFormError(err.response?.data?.error || "Eroare la salvarea acordului prin document.");
    } finally {
      setIsSubmittingDoc(false);
    }
  };

  // Compute Active Filter Count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== "all") count++;
    if (selectedChannels.length > 0) count += selectedChannels.length;
    return count;
  }, [statusFilter, selectedChannels]);

  const handleResetFilters = () => {
    setStatusFilter("all");
    setSelectedChannels([]);
    setSearchQuery("");
    setCurrentPage(1);
  };

  // Open Assignment Popup
  const handleOpenAssign = (consent) => {
    if (!consent || !consent.is_confirmed) {
      return;
    }
    setTargetConsent(consent);
    if (consent.pharmacist_id) {
      setSelectedPersonUniqueId(`pharmacist_${consent.pharmacist_id}`);
    } else if (consent.physician_id) {
      setSelectedPersonUniqueId(`physician_${consent.physician_id}`);
    } else {
      setSelectedPersonUniqueId(null);
    }
    setAssignSearch("");
    setAssignModalOpen(true);
  };

  // Save Link
  const handleSaveLink = async (personUniqueIdToSave) => {
    if (!targetConsent || (!targetConsent.is_confirmed && personUniqueIdToSave)) return;
    setIsSavingLink(true);
    try {
      const matchedPerson = physiciansList.find((p) => p.unique_id === personUniqueIdToSave);
      const physician_id = matchedPerson?.person_type === "physician" ? matchedPerson.physician_id : null;
      const pharmacist_id = matchedPerson?.person_type === "pharmacist" ? matchedPerson.pharmacist_id : null;

      // Instant optimistic UI update for instant tab counts & table update
      setConsents((prev) =>
        prev.map((item) =>
          item.consent_id === targetConsent.consent_id
            ? {
                ...item,
                physician_id,
                pharmacist_id,
                linked_person_type: matchedPerson ? matchedPerson.person_type : null,
                linked_physician_name: matchedPerson ? matchedPerson.user_name_surname : null,
                linked_physician_phone: matchedPerson ? matchedPerson.phone : null,
                linked_physician_email: matchedPerson ? matchedPerson.email : null,
              }
            : item
        )
      );

      await axios.put(
        `${API_URL}/api/marketing-consent/${targetConsent.consent_id}/link-physician`,
        { physician_id, pharmacist_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const actionDesc = matchedPerson
        ? `A asociat acordul de date (${targetConsent.last_name || ""} ${targetConsent.first_name || ""}) cu ${matchedPerson.person_type === "pharmacist" ? "farmacistul" : "medicul"} ${matchedPerson.user_name_surname}`
        : `A șters asocierea acordului de date (${targetConsent.last_name || ""} ${targetConsent.first_name || ""})`;

      logAuditUI({
        actionType: matchedPerson ? "UPDATE" : "DELETE",
        module: "Acord de date",
        entityType: "marketing_consent",
        entityId: targetConsent.consent_id,
        entityName: `${targetConsent.last_name || ""} ${targetConsent.first_name || ""}`.trim(),
        description: actionDesc,
        details: {
          consent_id: targetConsent.consent_id,
          linked_to: matchedPerson?.user_name_surname,
          person_type: matchedPerson?.person_type,
          physician_id,
          pharmacist_id,
        },
      });

      setAssignModalOpen(false);
      await fetchConsents(); // Refresh list to ensure full sync with DB
    } catch (err) {
      console.error("Error linking person to consent:", err);
      fetchConsents();
    } finally {
      setIsSavingLink(false);
    }
  };

  // Quick Unlink (removes link and returns to unlinked status)
  const handleUnlinkPhysician = async (consent) => {
    if (!consent) return;
    try {
      // Instant optimistic UI update
      setConsents((prev) =>
        prev.map((item) =>
          item.consent_id === consent.consent_id
            ? {
                ...item,
                physician_id: null,
                pharmacist_id: null,
                linked_person_type: null,
                linked_physician_name: null,
                linked_physician_phone: null,
                linked_physician_email: null,
              }
            : item
        )
      );

      await axios.put(
        `${API_URL}/api/marketing-consent/${consent.consent_id}/link-physician`,
        { physician_id: null, pharmacist_id: null },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      logAuditUI({
        actionType: "DELETE",
        module: "Acord de date",
        entityType: "marketing_consent",
        entityId: consent.consent_id,
        entityName: `${consent.last_name || ""} ${consent.first_name || ""}`.trim(),
        description: `A șters asocierea acordului de date pentru ${consent.last_name || ""} ${consent.first_name || ""}${consent.linked_physician_name ? ` (anterior asociat cu: ${consent.linked_physician_name})` : ""}`,
        details: {
          consent_id: consent.consent_id,
          previous_linked_name: consent.linked_physician_name,
        },
      });

      await fetchConsents();
    } catch (err) {
      console.error("Error unlinking person:", err);
      fetchConsents();
    }
  };

  // Filter & Sort Logic
  const filteredAndSorted = useMemo(() => {
    let result = [...consents];

    // Main Tab Filter
    if (activeTab === "linked") {
      result = result.filter((c) => Boolean(c.physician_id || c.pharmacist_id));
    } else if (activeTab === "unlinked") {
      result = result.filter((c) => !c.physician_id && !c.pharmacist_id);
    }

    // Status filter
    if (statusFilter === "active" || statusFilter === "confirmed") {
      result = result.filter((c) => c.marketing_consent !== false && c.marketing_consent !== 0 && Boolean(c.is_confirmed));
    } else if (statusFilter === "unsubscribed") {
      result = result.filter((c) => c.marketing_consent === false || c.marketing_consent === 0);
    } else if (statusFilter === "pending") {
      result = result.filter((c) => c.marketing_consent !== false && c.marketing_consent !== 0 && !c.is_confirmed);
    }

    // Channel filter
    if (selectedChannels.length > 0) {
      result = result.filter((c) => {
        const channels = parseJsonArray(c.communication_channels);
        return selectedChannels.some((ch) => channels.includes(ch));
      });
    }

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((c) => {
        const fullName = `${c.last_name || ""} ${c.first_name || ""}`.toLowerCase();
        const spec = (c.specialization || "").toLowerCase();
        const unit = (c.medical_unit || "").toLowerCase();
        const phone = (c.phone || "").toLowerCase();
        const email = (c.email || "").toLowerCase();
        const linkedName = (c.linked_physician_name || "").toLowerCase();
        return (
          fullName.includes(q) ||
          spec.includes(q) ||
          unit.includes(q) ||
          phone.includes(q) ||
          email.includes(q) ||
          linkedName.includes(q)
        );
      });
    }

    // Sorting
    result.sort((a, b) => {
      if (sortBy === "recent") {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      if (sortBy === "oldest") {
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      }
      if (sortBy === "name_asc") {
        const nameA = `${a.last_name || ""} ${a.first_name || ""}`.trim();
        const nameB = `${b.last_name || ""} ${b.first_name || ""}`.trim();
        return nameA.localeCompare(nameB);
      }
      if (sortBy === "name_desc") {
        const nameA = `${a.last_name || ""} ${a.first_name || ""}`.trim();
        const nameB = `${b.last_name || ""} ${b.first_name || ""}`.trim();
        return nameB.localeCompare(nameA);
      }
      return 0;
    });

    return result;
  }, [consents, activeTab, statusFilter, selectedChannels, searchQuery, sortBy]);

  // Overall Stats
  const stats = useMemo(() => {
    const total = consents.length;
    const unsubscribed = consents.filter((c) => c.marketing_consent === false || c.marketing_consent === 0).length;
    const confirmed = consents.filter((c) => c.marketing_consent !== false && c.marketing_consent !== 0 && c.is_confirmed).length;
    const pending = consents.filter((c) => c.marketing_consent !== false && c.marketing_consent !== 0 && !c.is_confirmed).length;
    const linked = consents.filter((c) => Boolean(c.physician_id || c.pharmacist_id)).length;
    const unlinked = total - linked;
    const confirmedPct = total > 0 ? ((confirmed / total) * 100).toFixed(1) : "0.0";
    const pendingPct = total > 0 ? ((pending / total) * 100).toFixed(1) : "0.0";
    const unsubscribedPct = total > 0 ? ((unsubscribed / total) * 100).toFixed(1) : "0.0";
    return { total, confirmed, pending, unsubscribed, linked, unlinked, confirmedPct, pendingPct, unsubscribedPct };
  }, [consents]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage) || 1;
  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSorted.slice(start, start + itemsPerPage);
  }, [filteredAndSorted, currentPage, itemsPerPage]);

  // Filter assignable persons in assignment modal with accurate smart matching
  const modalFilteredPersons = useMemo(() => {
    if (!targetConsent) return [];

    const q = normalizeText(assignSearch);
    const consentLastName = normalizeText(targetConsent.last_name);
    const consentFirstName = normalizeText(targetConsent.first_name);
    const consentPhone = (targetConsent.phone || "").replace(/\D/g, "");
    const consentEmail = normalizeText(targetConsent.email);

    const cLastWords = extractWords(targetConsent.last_name);
    const cFirstWords = extractWords(targetConsent.first_name);
    const cSpecWords = extractWords(targetConsent.specialization);
    const cInstWords = extractWords(targetConsent.medical_unit);

    return physiciansList
      .map((p) => {
        const pName = normalizeText(p.user_name_surname);
        const pWords = extractWords(p.user_name_surname);
        const pPhone = (p.phone ? String(p.phone) : "").replace(/\D/g, "");
        const pEmail = normalizeText(p.email);
        const pSpec = normalizeText(p.description);
        const pSpecWords = extractWords(p.description);
        const pInst = normalizeText(p.inst);
        const pInstWords = extractWords(p.inst);
        const pCity = normalizeText(p.city_name);

        // 1. Phone Match (last 8 digits)
        const pPhone8 = pPhone.slice(-8);
        const cPhone8 = consentPhone.slice(-8);
        const isPhoneMatch =
          pPhone8.length >= 8 && cPhone8.length >= 8 && pPhone8 === cPhone8;

        // 2. Email Match
        const isEmailMatch = Boolean(
          consentEmail && pEmail && consentEmail === pEmail
        );

        // 3. Name Word Matches (token level, not substring of another word)
        const hasLastNameMatch = cLastWords.some((cw) =>
          pWords.some((pw) => pw === cw || (cw.length >= 4 && (pw.startsWith(cw) || cw.startsWith(pw))))
        );

        const hasFirstNameMatch = cFirstWords.some((cw) =>
          pWords.some((pw) => pw === cw || (cw.length >= 3 && (pw.startsWith(cw) || cw.startsWith(pw))))
        );

        const isFullNameMatch = hasLastNameMatch && hasFirstNameMatch;

        // 4. Specialty / Institution Overlap
        const hasSpecialtyOverlap =
          cSpecWords.length > 0 &&
          pSpecWords.some((pw) =>
            cSpecWords.some((cw) => pw === cw || (cw.length >= 4 && pw.startsWith(cw)) || (pw.length >= 4 && cw.startsWith(pw)))
          );

        const hasInstOverlap =
          cInstWords.length > 0 &&
          pInstWords.some((pw) =>
            cInstWords.some(
              (cw) =>
                pw.length >= 4 &&
                cw.length >= 4 &&
                (pw === cw || pw.includes(cw) || cw.includes(pw))
            )
          );

        // Calculate score & accurate isSuggested
        let isSuggested = false;
        let matchScore = 0;

        if (isPhoneMatch) {
          isSuggested = true;
          matchScore += 100;
        }
        if (isEmailMatch) {
          isSuggested = true;
          matchScore += 90;
        }
        if (isFullNameMatch) {
          isSuggested = true;
          matchScore += 80;
        } else if (hasLastNameMatch && (hasSpecialtyOverlap || hasInstOverlap)) {
          isSuggested = true;
          matchScore += 60;
        } else if (hasLastNameMatch && cFirstWords.length === 0 && consentLastName.length >= 4) {
          isSuggested = true;
          matchScore += 40;
        }

        const qDigits = q.replace(/\D/g, "");
        const matchesSearch =
          !q ||
          pName.includes(q) ||
          pSpec.includes(q) ||
          pInst.includes(q) ||
          pCity.includes(q) ||
          (qDigits && pPhone.includes(qDigits));

        return { ...p, isSuggested, matchScore, matchesSearch };
      })
      .filter((p) => p.matchesSearch)
      .sort((a, b) => {
        // Prioritize currently selected person
        if (a.unique_id === selectedPersonUniqueId) return -1;
        if (b.unique_id === selectedPersonUniqueId) return 1;
        // Prioritize suggested matches with highest score
        if (a.matchScore !== b.matchScore) return b.matchScore - a.matchScore;
        if (a.isSuggested && !b.isSuggested) return -1;
        if (!a.isSuggested && b.isSuggested) return 1;
        // Then alphabetical
        return (a.user_name_surname || "").localeCompare(b.user_name_surname || "");
      });
  }, [physiciansList, targetConsent, assignSearch, selectedPersonUniqueId]);

  // Export CSV
  const handleExportCSV = () => {
    if (filteredAndSorted.length === 0) return;

    logAuditUI({
      actionType: "EXPORT",
      module: "Acord de date",
      description: `A downloadat / exportat ${filteredAndSorted.length} acorduri de date în format CSV`,
      details: {
        total_records_exported: filteredAndSorted.length,
        filter_status: statusFilter,
        filter_tab: activeTab,
      },
    });

    const headers = [
      "ID",
      "Nume",
      "Prenume",
      "Specializare",
      "Organizație",
      "Telefon",
      "Email",
      "Medic Asociat CRM",
      "Status Acord",
      "Data Înregistrării",
      "Data Confirmării",
    ];

    const rows = filteredAndSorted.map((c) => {
      return [
        c.consent_id,
        `"${(c.last_name || "").replace(/"/g, '""')}"`,
        `"${(c.first_name || "").replace(/"/g, '""')}"`,
        `"${(c.specialization || "").replace(/"/g, '""')}"`,
        `"${(c.medical_unit || "").replace(/"/g, '""')}"`,
        `"${(c.phone || "").replace(/"/g, '""')}"`,
        `"${(c.email || "").replace(/"/g, '""')}"`,
        `"${(c.linked_physician_name || "Neatribuit").replace(/"/g, '""')}"`,
        c.is_confirmed ? "Confirmat" : "În așteptare",
        c.created_at ? dayjs(c.created_at).format("YYYY-MM-DD HH:mm:ss") : "",
        c.confirmed_at ? dayjs(c.confirmed_at).format("YYYY-MM-DD HH:mm:ss") : "",
      ].join(",");
    });

    const csvContent = "\uFEFF" + [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Acorduri_Date_Marketing_${dayjs().format("YYYYMMDD_HHmm")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8FAFC] dark:bg-gray-900 font-sans">
      {/* Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Main Container */}
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden min-h-0">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-3 sm:px-6 lg:px-8 py-6 pb-16 w-full max-w-full mx-auto">
            {/* Top Row: Page Title & 3 KPI Metric Cards */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-6">
              {/* Left: Title & Subtitle */}
              <div>
                <h1 className="text-[28px] sm:text-[32px] font-extrabold text-[#0F172A] dark:text-gray-100 leading-tight tracking-tight">
                  Acord de date
                </h1>
                <p className="text-sm text-[#64748B] dark:text-gray-400 mt-1">
                  Gestionează și verifică acordurile de contact și marketing.
                </p>
              </div>

              {/* Right: 3 KPI Metric Cards in Row */}
              <div className="flex flex-wrap items-center gap-3.5 sm:gap-4">
                {/* Metric Card 1: Total înregistrări */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-3.5 px-4 sm:px-5 border border-gray-100 dark:border-gray-700/60 shadow-[0_2px_10px_rgba(15,23,42,0.03)] flex items-center gap-3.5 min-w-[160px] sm:min-w-[175px]">
                  <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] text-[#6366F1] flex items-center justify-center shrink-0">
                    <HiOutlineUsers className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[22px] font-extrabold text-[#0F172A] dark:text-gray-100 leading-none">
                      {stats.total}
                    </div>
                    <div className="text-[11px] font-medium text-[#64748B] dark:text-gray-400 mt-1">
                      Total înregistrări
                    </div>
                  </div>
                </div>

                {/* Metric Card 2: Confirmate */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-3.5 px-4 sm:px-5 border border-gray-100 dark:border-gray-700/60 shadow-[0_2px_10px_rgba(15,23,42,0.03)] flex items-center justify-between gap-3 min-w-[190px] sm:min-w-[205px]">
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-[#ECFDF5] text-[#10B981] flex items-center justify-center shrink-0">
                      <HiOutlineCheckCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[22px] font-extrabold text-[#0F172A] dark:text-gray-100 leading-none">
                        {stats.confirmed}
                      </div>
                      <div className="text-[11px] font-medium text-[#64748B] dark:text-gray-400 mt-1">
                        Confirmate
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#ECFDF5] text-[#059669] dark:bg-emerald-950/40 dark:text-emerald-400">
                    {stats.confirmedPct}%
                  </div>
                </div>

                {/* Metric Card 3: În așteptare */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-3.5 px-4 sm:px-5 border border-gray-100 dark:border-gray-700/60 shadow-[0_2px_10px_rgba(15,23,42,0.03)] flex items-center justify-between gap-3 min-w-[190px] sm:min-w-[205px]">
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-[#FFFBEB] text-[#F59E0B] flex items-center justify-center shrink-0">
                      <HiOutlineClock className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[22px] font-extrabold text-[#0F172A] dark:text-gray-100 leading-none">
                        {stats.pending}
                      </div>
                      <div className="text-[11px] font-medium text-[#64748B] dark:text-gray-400 mt-1">
                        În așteptare
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#FEF3C7] text-[#D97706] dark:bg-amber-950/40 dark:text-amber-400">
                    {stats.pendingPct}%
                  </div>
                </div>

                {/* Metric Card 4: Dezabonate (Negativ) */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-3.5 px-4 sm:px-5 border border-gray-100 dark:border-gray-700/60 shadow-[0_2px_10px_rgba(15,23,42,0.03)] flex items-center justify-between gap-3 min-w-[190px] sm:min-w-[205px]">
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-[#FEF2F2] text-[#EF4444] flex items-center justify-center shrink-0">
                      <HiOutlineXCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[22px] font-extrabold text-[#0F172A] dark:text-gray-100 leading-none">
                        {stats.unsubscribed}
                      </div>
                      <div className="text-[11px] font-medium text-[#64748B] dark:text-gray-400 mt-1">
                        Dezabonate (Negativ)
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#FEF2F2] text-[#B91C1C] dark:bg-red-950/40 dark:text-red-400">
                    {stats.unsubscribedPct}%
                  </div>
                </div>
              </div>
            </div>

            {/* Toolbar Row: Search, Tabs (Toate, Medici confirmați/asociați, Neatribuite), Filtrează, Sort */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3.5 mb-5 mt-7">
              {/* Left side: Search & Filter controls + Tabs */}
              <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                {/* Search input */}
                <div className="relative w-full sm:w-[280px] md:w-[320px]">
                  <HiOutlineMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                  <input
                    type="text"
                    className="w-full h-10 pl-10 pr-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/15 transition-all"
                    placeholder="Caută după nume, email, specialitate..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>

                {/* Filter Popover Button */}
                <Popover className="relative">
                  {({ open }) => (
                    <>
                      <Popover.Button
                        className={`h-10 px-3.5 sm:px-4 rounded-xl border text-xs sm:text-sm font-medium inline-flex items-center gap-2 shadow-xs transition-colors cursor-pointer ${
                          open || activeFiltersCount > 0
                            ? "border-[#6366F1] bg-[#F5F3FF] text-[#6366F1] dark:bg-indigo-950/40 dark:border-indigo-800 dark:text-indigo-300"
                            : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-[#475569] dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60"
                        }`}
                      >
                        <HiOutlineFunnel className="w-4 h-4 text-[#6366F1]" />
                        <span>Filtrează</span>
                        {activeFiltersCount > 0 && (
                          <span className="w-5 h-5 rounded-full bg-[#6366F1] text-white text-[11px] font-bold inline-flex items-center justify-center">
                            {activeFiltersCount}
                          </span>
                        )}
                      </Popover.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-150"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel className="absolute left-0 z-30 mt-2 w-72 rounded-2xl bg-white dark:bg-gray-800 p-4 shadow-xl border border-gray-100 dark:border-gray-700 text-xs">
                          <div className="space-y-4">
                            {/* Status Filter */}
                            <div>
                              <span className="font-bold text-[#0F172A] dark:text-gray-200 block mb-2">
                                Status Confirmare
                              </span>
                              <div className="space-y-1.5">
                                {[
                                  { id: "all", label: "Toate statusurile" },
                                  { id: "active", label: "Activ (Pozitiv)" },
                                  { id: "unsubscribed", label: "Dezabonat (Negativ)" },
                                  { id: "pending", label: "În așteptare (Cod neintrodus)" },
                                ].map((opt) => (
                                  <label
                                    key={opt.id}
                                    className="flex items-center gap-2 text-[#334155] dark:text-gray-300 cursor-pointer hover:text-[#0F172A]"
                                  >
                                    <input
                                      type="radio"
                                      name="status_filter"
                                      className="text-[#6366F1] focus:ring-[#6366F1]"
                                      checked={statusFilter === opt.id}
                                      onChange={() => {
                                        setStatusFilter(opt.id);
                                        setCurrentPage(1);
                                      }}
                                    />
                                    <span>{opt.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            {/* Channel Filter */}
                            <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60">
                              <span className="font-bold text-[#0F172A] dark:text-gray-200 block mb-2">
                                Canale de comunicare
                              </span>
                              <div className="space-y-1.5">
                                {[
                                  { id: "document", label: "Document fizic" },
                                  { id: "email", label: "Email" },
                                  { id: "sms", label: "SMS" },
                                  { id: "phone", label: "Telefon (Apel)" },
                                ].map((ch) => (
                                  <label
                                    key={ch.id}
                                    className="flex items-center gap-2 text-[#334155] dark:text-gray-300 cursor-pointer hover:text-[#0F172A]"
                                  >
                                    <input
                                      type="checkbox"
                                      className="rounded text-[#6366F1] focus:ring-[#6366F1]"
                                      checked={selectedChannels.includes(ch.id)}
                                      onChange={() => {
                                        setSelectedChannels((prev) =>
                                          prev.includes(ch.id) ? prev.filter((i) => i !== ch.id) : [...prev, ch.id]
                                        );
                                        setCurrentPage(1);
                                      }}
                                    />
                                    <span>{ch.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            {/* Clear filter inside popover */}
                            <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 flex justify-between items-center">
                              <button
                                type="button"
                                onClick={handleResetFilters}
                                className="text-[11px] font-medium text-rose-500 hover:text-rose-600 cursor-pointer"
                              >
                                Șterge filtrele
                              </button>
                            </div>
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </>
                  )}
                </Popover>

                {/* View Tabs next to Filtrează: Toate | Medici asociați/confirmați | Neatribuite */}
                <div className="bg-white dark:bg-gray-800 p-1 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs flex items-center">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab("all");
                      setCurrentPage(1);
                    }}
                    className={`h-8 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                      activeTab === "all"
                        ? "bg-[#6366F1] text-white shadow-xs"
                        : "text-[#64748B] hover:text-[#0F172A] dark:text-gray-300"
                    }`}
                  >
                    Toate ({stats.total})
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab("linked");
                      setCurrentPage(1);
                    }}
                    className={`h-8 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                      activeTab === "linked"
                        ? "bg-[#10B981] text-white shadow-xs"
                        : "text-[#64748B] hover:text-[#10B981] dark:text-gray-300"
                    }`}
                  >
                    <span>Asociați</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                        activeTab === "linked"
                          ? "bg-white/25 text-white"
                          : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400"
                      }`}
                    >
                      {stats.linked}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab("unlinked");
                      setCurrentPage(1);
                    }}
                    className={`h-8 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                      activeTab === "unlinked"
                        ? "bg-[#F59E0B] text-white shadow-xs"
                        : "text-[#64748B] hover:text-[#F59E0B] dark:text-gray-300"
                    }`}
                  >
                    <span>Neatribuite</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                        activeTab === "unlinked"
                          ? "bg-white/25 text-white"
                          : "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400"
                      }`}
                    >
                      {stats.unlinked}
                    </span>
                  </button>
                </div>

                {/* Resetează filtrele text link */}
                {(activeFiltersCount > 0 || searchQuery || activeTab !== "all") && (
                  <button
                    type="button"
                    onClick={() => {
                      handleResetFilters();
                      setActiveTab("all");
                    }}
                    className="text-xs font-medium text-[#64748B] hover:text-[#6366F1] dark:text-gray-400 dark:hover:text-indigo-400 cursor-pointer transition-colors"
                  >
                    Resetează filtrele
                  </button>
                )}
              </div>

              {/* Right side: Actions (Upload Document, Export CSV, Sort) */}
              <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
                {/* Buton Nou: Adaugă acord prin document scanat / fizic */}
                <button
                  type="button"
                  onClick={() => setUploadModalOpen(true)}
                  className="h-10 px-3.5 sm:px-4 rounded-xl bg-gradient-to-r from-[#6366F1] to-[#4F46E5] hover:from-[#4F46E5] hover:to-[#4338CA] text-white text-xs sm:text-sm font-semibold inline-flex items-center gap-2 shadow-xs shadow-indigo-500/20 transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                  title="Adaugă un acord de date prin încărcarea documentului fizic semnat olograf (scanat / foto)"
                >
                  <HiOutlineDocumentArrowUp className="w-4 h-4 stroke-2" />
                  <span>Adaugă acord prin document</span>
                </button>

                <button
                  type="button"
                  onClick={handleExportCSV}
                  className="h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-[#475569] dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 inline-flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                  title="Exportă tabelul în CSV"
                >
                  <HiOutlineArrowDownTray className="w-4 h-4 text-gray-500" />
                  <span className="hidden sm:inline">Exportă CSV</span>
                </button>

                {/* Sort Dropdown */}
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="h-10 pl-3.5 pr-9 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs sm:text-sm font-medium text-[#475569] dark:text-gray-200 shadow-xs focus:outline-none focus:border-[#6366F1] cursor-pointer appearance-none"
                  >
                    <option value="recent">↑↓ Cele mai recente</option>
                    <option value="oldest">↑↓ Cele mai vechi</option>
                    <option value="name_asc">Nume (A - Z)</option>
                    <option value="name_desc">Nume (Z - A)</option>
                  </select>
                  <HiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Main Table Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/80 dark:border-gray-700/60 shadow-[0_2px_12px_rgba(15,23,42,0.03)] overflow-hidden">
              {loading ? (
                <div className="py-20 text-center">
                  <div className="inline-block w-8 h-8 border-3 border-[#6366F1] border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-xs text-[#64748B] dark:text-gray-400 mt-2 font-medium">
                    Se încarcă datele...
                  </p>
                </div>
              ) : currentItems.length > 0 ? (
                <div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-full text-left">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-gray-700/60 text-[#64748B] dark:text-gray-400 text-xs font-semibold">
                          {/* Column 1: Attribution Badge (! / ✓) */}
                          <th className="w-12 px-3 py-3.5 text-center">
                            <span title="Status asociere cu un medic sau farmacist din CRM">Asociat</span>
                          </th>
                          <th className="px-3 py-3.5">Nume și prenume</th>
                          <th className="px-3 py-3.5">Organizație</th>
                          <th className="px-3 py-3.5">Contact</th>
                          <th className="px-3 py-3.5">Canale</th>
                          <th className="px-3 py-3.5">Specialități</th>
                          <th className="px-3 py-3.5 text-center">Status</th>
                          <th className="px-3 py-3.5 text-center">Data</th>
                          <th className="px-3 py-3.5 text-center">Acțiuni</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-50 dark:divide-gray-700/40">
                        {currentItems.map((c) => {
                          const channels = parseJsonArray(c.communication_channels);
                          const areas = parseJsonArray(c.therapeutic_areas);
                          const fullName = `${c.last_name || ""} ${c.first_name || ""}`.trim() || "Nespecificat";
                          const isLinked = Boolean(c.physician_id || c.pharmacist_id);
                          const personTypeLabel = c.linked_person_type === "pharmacist" || c.pharmacist_id ? "Farmacist" : "Medic";
                          const scannedDocs = getScannedDocuments(c);
                          const hasScannedDocs = scannedDocs.length > 0;

                          return (
                            <tr
                              key={c.consent_id}
                              className="hover:bg-[#F8FAFC]/80 dark:hover:bg-gray-700/30 transition-colors"
                            >
                              {/* Column 1: Attribution Button (!) sau (✓) */}
                              <td className="px-3 py-3 text-center whitespace-nowrap">
                                {isLinked ? (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenAssign(c)}
                                    className="w-8 h-8 rounded-xl bg-[#DCFCE7] hover:bg-[#BBF7D0] text-[#15803D] border-2 border-[#22C55E] dark:bg-emerald-950/70 dark:text-emerald-300 dark:border-emerald-600 inline-flex items-center justify-center font-extrabold text-sm shadow-xs transition-all hover:scale-105 cursor-pointer"
                                    title={`Atribuit: ${c.linked_physician_name || "Persoană CRM"} (${personTypeLabel}). Click pentru a schimba/gestiona.`}
                                  >
                                    <HiOutlineCheck className="w-4 h-4 stroke-[3]" />
                                  </button>
                                ) : c.is_confirmed ? (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenAssign(c)}
                                    className="w-8 h-8 rounded-xl bg-[#FEF3C7] hover:bg-[#FDE68A] text-[#B45309] border-2 border-[#F59E0B] dark:bg-amber-950/70 dark:text-amber-300 dark:border-amber-600 inline-flex items-center justify-center font-black text-sm shadow-xs transition-all hover:scale-105 cursor-pointer"
                                    title="Acest acord NU a fost atribuit unui medic sau farmacist din baza de date. Click pentru asociere."
                                  >
                                    <span className="leading-none text-[15px] font-black">!</span>
                                  </button>
                                ) : (
                                  <div
                                    className="w-8 h-8 rounded-xl bg-slate-100 text-slate-400 border border-slate-200 dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700 inline-flex items-center justify-center cursor-not-allowed opacity-60"
                                    title="Cod neintrodus: Doar acordurile de date confirmate prin codul primit pe email pot fi atribuite."
                                  >
                                    <HiOutlineLockClosed className="w-4 h-4" />
                                  </div>
                                )}
                              </td>

                              {/* Nume și Prenume + Avatar + Specialitate / Medic/Farmacist Asociat */}
                              <td className="px-3 py-3">
                                <div className="flex items-center gap-2.5 min-w-[170px]">
                                  {/* Avatar Initials Badge */}
                                  <div className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-gray-700 text-[#1E293B] dark:text-gray-100 text-xs font-bold border border-slate-200/80 dark:border-gray-600 flex items-center justify-center shrink-0">
                                    {getInitials(c.first_name, c.last_name)}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-sm font-bold text-[#0F172A] dark:text-gray-100 leading-tight truncate">
                                      {fullName}
                                    </div>
                                    <div className="text-xs text-[#64748B] dark:text-gray-400 mt-0.5 font-normal flex flex-wrap items-center gap-1.5">
                                      <span className="truncate">{c.specialization || "—"}</span>
                                      {c.linked_physician_name && (
                                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-1.5 py-0.2 rounded-md border border-emerald-200/60 dark:border-emerald-800/40 truncate max-w-[180px]">
                                          <HiOutlineLink className="w-3 h-3 shrink-0" />
                                          <span className="truncate">{c.linked_physician_name}</span>
                                          <span className="text-[10px] opacity-75 shrink-0">({personTypeLabel})</span>
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* Organizație / Unitate Medicală */}
                              <td className="px-3 py-3">
                                <div className="max-w-[210px]">
                                  <span
                                    className="text-xs font-semibold text-[#334155] dark:text-gray-300 line-clamp-2 leading-snug"
                                    title={c.medical_unit || ""}
                                  >
                                    {c.medical_unit || "—"}
                                  </span>
                                </div>
                              </td>

                              {/* Contact: Email & Telefon */}
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="text-xs space-y-1">
                                  {c.email ? (
                                    <div className="text-[#334155] dark:text-gray-300 flex items-center gap-1.5 font-medium">
                                      <HiOutlineEnvelope className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                      <span className="truncate max-w-[190px]" title={c.email}>{c.email}</span>
                                    </div>
                                  ) : null}
                                  {c.phone ? (
                                    <div className="text-[#64748B] dark:text-gray-400 flex items-center gap-1.5 font-medium">
                                      <HiOutlinePhone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                      <span>{c.phone}</span>
                                    </div>
                                  ) : null}
                                </div>
                              </td>

                              {/* Canale: Pastel badges (Document, Email, SMS, Telefon) */}
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  {channels.includes("document") && (
                                    hasScannedDocs ? (
                                      <button
                                        type="button"
                                        onClick={() => handleOpenDocViewer(c)}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-[#ECFDF5] text-[#047857] border border-emerald-300 hover:bg-emerald-100 hover:border-emerald-400 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 shadow-2xs transition-all cursor-pointer hover:scale-105 active:scale-95"
                                        title="Document atașat. Click pentru a vizualiza / citi fișierul."
                                      >
                                        <HiOutlineDocumentText className="w-3.5 h-3.5" />
                                        <span>Document</span>
                                      </button>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-[#ECFDF5] text-[#047857] border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/40" title="Document semnat pe suport de hârtie">
                                        <HiOutlineDocumentText className="w-3.5 h-3.5" />
                                        Document
                                      </span>
                                    )
                                  )}
                                  {channels.includes("email") && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-[#EFF6FF] text-[#1D4ED8] border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800/40">
                                      Email
                                    </span>
                                  )}
                                  {channels.includes("sms") && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-[#FFF7ED] text-[#C2410C] border border-orange-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/40">
                                      SMS
                                    </span>
                                  )}
                                  {channels.includes("phone") && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-[#FAF5FF] text-[#6D28D9] border border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800/40">
                                      Telefon
                                    </span>
                                  )}
                                  {channels.length === 0 && (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </div>
                              </td>

                              {/* Specialități (Arii terapeutice) */}
                              <td className="px-3 py-3">
                                <div className="space-y-1 max-w-[200px]">
                                  {areas.length > 0 ? (
                                    <>
                                      <div className="text-xs text-[#334155] dark:text-gray-300 font-medium truncate" title={THERAPEUTIC_AREAS_MAP[areas[0]] || areas[0]}>
                                        {THERAPEUTIC_AREAS_MAP[areas[0]] || areas[0]}
                                      </div>
                                      {areas.length > 1 && (
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-xs text-[#334155] dark:text-gray-300 font-medium truncate" title={THERAPEUTIC_AREAS_MAP[areas[1]] || areas[1]}>
                                            {THERAPEUTIC_AREAS_MAP[areas[1]] || areas[1]}
                                          </span>
                                          {areas.length > 2 && (
                                            <span
                                              onClick={() => handleOpenDetails(c)}
                                              className="inline-flex items-center justify-center px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-slate-200/80 hover:bg-[#EEF2FF] text-slate-700 hover:text-[#6366F1] dark:bg-gray-700 dark:text-gray-300 cursor-pointer transition-colors"
                                              title={areas.slice(2).map((a) => THERAPEUTIC_AREAS_MAP[a] || a).join(", ")}
                                            >
                                              +{areas.length - 2}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </div>
                              </td>

                              {/* Status Confirmare */}
                              <td className="px-3 py-3 text-center whitespace-nowrap">
                                {c.marketing_consent === false || c.marketing_consent === 0 ? (
                                  <span
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FEF2F2] text-[#B91C1C] border border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800/40"
                                    title="Acordul a fost retras / persoana s-a dezabonat (Negativ)."
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444]"></span>
                                    Dezabonat (Negativ)
                                  </span>
                                ) : c.is_confirmed ? (
                                  <span
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#ECFDF5] text-[#047857] border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/40"
                                    title="Acordul este activ și confirmat (Pozitiv)."
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></span>
                                    Activ (Pozitiv)
                                  </span>
                                ) : (
                                  <span
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FFF7ED] text-[#C2410C] border border-orange-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/40"
                                    title="Formularul a fost completat, dar persoana nu a introdus încă codul de 6 cifre primit pe email."
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]"></span>
                                    Cod neintrodus
                                  </span>
                                )}
                              </td>

                              {/* Data & Ora */}
                              <td className="px-3 py-3 text-center whitespace-nowrap">
                                <div className="text-xs font-bold text-[#0F172A] dark:text-gray-200">
                                  {c.created_at ? dayjs(c.created_at).format("DD.MM.YYYY") : "—"}
                                </div>
                                <div className="text-[11px] text-[#64748B] dark:text-gray-400 mt-0.5 font-medium">
                                  {c.created_at ? dayjs(c.created_at).format("HH:mm") : ""}
                                </div>
                              </td>

                              {/* Acțiuni: Document Reader + Eye Icon + Buton Roșu Ștergere Legătură */}
                              <td className="px-3 py-3 text-center whitespace-nowrap">
                                <div className="inline-flex items-center justify-center gap-1.5">
                                  {hasScannedDocs && (
                                    <button
                                      type="button"
                                      onClick={() => handleOpenDocViewer(c)}
                                      className="w-8 h-8 rounded-xl bg-[#ECFDF5] hover:bg-[#10B981] text-[#047857] hover:text-white border border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-700 shadow-xs inline-flex items-center justify-center transition-all duration-150 cursor-pointer"
                                      title="Deschide și citește documentul fizic scanat"
                                    >
                                      <HiOutlineDocumentText className="w-4 h-4 stroke-2" />
                                    </button>
                                  )}

                                   {canEdit && (
                                    <button
                                      type="button"
                                      onClick={() => handleOpenEdit(c)}
                                      className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-[#252a32] text-[#252a32] hover:text-white border border-slate-300 dark:bg-gray-800 dark:hover:bg-[#252a32] dark:text-gray-300 dark:hover:text-white dark:border-gray-700 shadow-xs inline-flex items-center justify-center transition-all duration-150 cursor-pointer"
                                      title="Editează / Corectează datele acordului"
                                    >
                                      <HiOutlinePencilSquare className="w-4 h-4 stroke-2" />
                                    </button>
                                  )}

                                  <button
                                    type="button"
                                    onClick={() => handleOpenDetails(c)}
                                    className="w-8 h-8 rounded-xl bg-[#EEF2FF] hover:bg-[#6366F1] text-[#4F46E5] hover:text-white border border-[#C7D2FE] shadow-xs inline-flex items-center justify-center transition-all duration-150 cursor-pointer"
                                    title="Vezi detalii complete acord"
                                  >
                                    <HiOutlineEye className="w-4 h-4 stroke-2" />
                                  </button>

                                  {isLinked && (
                                    <button
                                      type="button"
                                      onClick={() => handleUnlinkPhysician(c)}
                                      className="w-8 h-8 rounded-xl bg-[#FEF2F2] hover:bg-[#DC2626] text-[#DC2626] hover:text-white border border-[#FECACA] dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-900/60 shadow-xs inline-flex items-center justify-center transition-all duration-150 cursor-pointer"
                                      title="Șterge legătura (revine la Neatribuit)"
                                    >
                                      <HiOutlineTrash className="w-4 h-4 stroke-2" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Footer matching screenshot */}
                  <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700/60 flex flex-col sm:flex-row items-center justify-between gap-4">
                    {/* Left side: Total count */}
                    <span className="text-xs sm:text-sm font-normal text-[#64748B] dark:text-gray-400">
                      Total: {filteredAndSorted.length} {filteredAndSorted.length === 1 ? "înregistrare" : "înregistrări"}
                    </span>

                    {/* Center: Pagination numbers with << < 1 2 3 > >> */}
                    <div className="flex items-center gap-1">
                      {/* First page button */}
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer"
                        title="Prima pagină"
                      >
                        <HiChevronDoubleLeft className="w-3.5 h-3.5" />
                      </button>

                      {/* Prev page button */}
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                        disabled={currentPage === 1}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer"
                        title="Pagina precedentă"
                      >
                        <HiChevronLeft className="w-3.5 h-3.5" />
                      </button>

                      {/* Numbered page buttons */}
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        const isActive = page === currentPage;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              type="button"
                              onClick={() => setCurrentPage(page)}
                              className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                                isActive
                                  ? "bg-[#6366F1] text-white shadow-xs"
                                  : "text-[#64748B] hover:text-[#0F172A] hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                              }`}
                            >
                              {page}
                            </button>
                          );
                        }
                        if (
                          (page === 2 && currentPage > 3) ||
                          (page === totalPages - 1 && currentPage < totalPages - 2)
                        ) {
                          return (
                            <span key={page} className="px-1 text-gray-400 text-xs">
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}

                      {/* Next page button */}
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer"
                        title="Pagina următoare"
                      >
                        <HiChevronRight className="w-3.5 h-3.5" />
                      </button>

                      {/* Last page button */}
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer"
                        title="Ultima pagină"
                      >
                        <HiChevronDoubleRight className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Right side: Items per page selector */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#64748B] dark:text-gray-400">Afișează:</span>
                      <div className="relative">
                        <select
                          className="h-8 pl-3 pr-8 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-[#334155] dark:text-gray-200 shadow-xs focus:outline-none focus:border-[#6366F1] cursor-pointer appearance-none"
                          value={itemsPerPage}
                          onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                        >
                          <option value={5}>5 pe pagină</option>
                          <option value={10}>10 pe pagină</option>
                          <option value={25}>25 pe pagină</option>
                          <option value={50}>50 pe pagină</option>
                          <option value={filteredAndSorted.length || 100}>Toate</option>
                        </select>
                        <HiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-20 px-6 text-center flex flex-col items-center justify-center">
                  <div className="w-12 h-12 rounded-2xl bg-[#EEF2FF] text-[#6366F1] flex items-center justify-center mb-3">
                    <HiOutlineUsers className="w-6 h-6" />
                  </div>
                  <h4 className="text-sm font-bold text-[#0F172A] dark:text-gray-100">
                    Nicio înregistrare găsită
                  </h4>
                  <p className="text-xs text-[#64748B] dark:text-gray-400 mt-1 max-w-sm">
                    {searchQuery || activeFiltersCount > 0 || activeTab !== "all"
                      ? "Nu există rezultate care să corespundă criteriilor sau filtrelor selectate."
                      : "Nu a fost găsit niciun consimțământ în baza de date."}
                  </p>
                  {(searchQuery || activeFiltersCount > 0 || activeTab !== "all") && (
                    <button
                      type="button"
                      onClick={() => {
                        handleResetFilters();
                        setActiveTab("all");
                      }}
                      className="mt-3.5 px-3.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs font-semibold text-[#334155] transition-colors cursor-pointer"
                    >
                      Resetează filtrele
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
        <Banner />
      </div>

      {/* POP-UP PREMIUM DESIGN: ASOCIERE ACORD DE DATE CU UN MEDIC SAU FARMACIST DIN CRM */}
      <Transition appear show={assignModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setAssignModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/45 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-3xl bg-white dark:bg-gray-800 p-6 sm:p-7 text-left align-middle shadow-2xl transition-all border border-gray-100 dark:border-gray-700">
                  {targetConsent && (
                    <div>
                      {/* Modal Top Header */}
                      <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-700/60 pb-4">
                        <div className="flex items-center gap-3.5">
                          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#6366F1] to-[#4F46E5] text-white flex items-center justify-center shadow-[0_4px_12px_rgba(99,102,241,0.25)] shrink-0">
                            <HiOutlineUserPlus className="w-6 h-6" />
                          </div>
                          <div>
                            <Dialog.Title as="h3" className="text-lg font-bold text-[#0F172A] dark:text-gray-100">
                              Asociere Acord de Date cu Medic / Farmacist
                            </Dialog.Title>
                            <p className="text-xs text-[#64748B] dark:text-gray-400 mt-0.5">
                              Asociază persoana din formularul de marketing cu un medic sau farmacist existent în CRM.
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setAssignModalOpen(false)}
                          className="w-9 h-9 rounded-xl inline-flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                        >
                          <HiOutlineXMark className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Consent Summary Pill Banner */}
                      <div className="mt-4 p-4 rounded-2xl bg-slate-50 dark:bg-gray-700/30 border border-slate-200/80 dark:border-gray-700/60">
                        <div className="text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider mb-1.5">
                          Date din Formularul de Consimțământ:
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <div className="text-sm font-bold text-[#0F172A] dark:text-gray-100">
                              {targetConsent.last_name} {targetConsent.first_name}
                            </div>
                            <div className="text-xs text-[#64748B] dark:text-gray-300">
                              {targetConsent.specialization || "Fără specializare"} • {targetConsent.medical_unit || "Fără unitate"}
                            </div>
                          </div>

                          <div className="text-right text-xs">
                            <div className="text-[#0F172A] dark:text-gray-200 font-medium">
                              {targetConsent.email || "Fără email"}
                            </div>
                            <div className="text-[#64748B] dark:text-gray-400">
                              {targetConsent.phone || "Fără telefon"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Search & Filter Bar */}
                      <div className="mt-5">
                        <div className="relative">
                          <HiOutlineMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                          <input
                            type="text"
                            className="w-full h-11 pl-10 pr-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/15 transition-all"
                            placeholder="Caută după nume, rol/specialitate, instituție, farmacie, oraș..."
                            value={assignSearch}
                            onChange={(e) => setAssignSearch(e.target.value)}
                            autoFocus
                          />
                        </div>
                      </div>

                      {/* Persons List Container */}
                      <div className="mt-3.5 max-h-[300px] overflow-y-auto space-y-2 pr-1 divide-y divide-transparent">
                        {loadingPhysicians ? (
                          <div className="py-12 text-center">
                            <div className="inline-block w-6 h-6 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-xs text-gray-400 mt-2 font-medium">Se încarcă lista de persoane...</p>
                          </div>
                        ) : modalFilteredPersons.length > 0 ? (
                          modalFilteredPersons.map((person) => {
                            const isSelected = selectedPersonUniqueId === person.unique_id;
                            const isPharmacist = person.person_type === "pharmacist";

                            return (
                              <div
                                key={person.unique_id}
                                onClick={() => setSelectedPersonUniqueId(person.unique_id)}
                                className={`p-3.5 rounded-2xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                                  isSelected
                                    ? "bg-[#F5F3FF] dark:bg-indigo-950/40 border-[#6366F1] shadow-[0_2px_8px_rgba(99,102,241,0.15)]"
                                    : "bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  {/* Avatar */}
                                  <div
                                    className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xs shrink-0 ${
                                      isSelected
                                        ? "bg-[#6366F1] text-white"
                                        : isPharmacist
                                        ? "bg-[#F3E8FF] text-[#7E22CE] dark:bg-purple-950/60 dark:text-purple-300"
                                        : "bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-gray-200"
                                    }`}
                                  >
                                    {getInitials(person.user_name_surname, "")}
                                  </div>

                                  <div>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-sm font-bold text-[#0F172A] dark:text-gray-100 flex items-center gap-1.5">
                                        <span>{person.user_name_surname}</span>
                                        {(person.data_acord === 1 || person.data_acord === true) && (
                                          <span title="Acord marketing confirmat" className="inline-flex items-center text-emerald-500 flex-shrink-0">
                                            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                          </span>
                                        )}
                                      </span>

                                      {/* Person Type Badge */}
                                      {isPharmacist ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#F3E8FF] text-[#7E22CE] dark:bg-purple-950/50 dark:text-purple-300">
                                          Farmacist
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#EEF2FF] text-[#4F46E5] dark:bg-indigo-950/50 dark:text-indigo-300">
                                          Medic
                                        </span>
                                      )}

                                      {person.isSuggested && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FEF3C7] text-[#D97706] dark:bg-amber-950/50 dark:text-amber-400">
                                          <HiOutlineSparkles className="w-3 h-3" />
                                          Potrivire sugerată
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-[#64748B] dark:text-gray-400 mt-0.5 flex flex-wrap items-center gap-x-2.5">
                                      <span>{person.description || (isPharmacist ? "Farmacist" : "Fără specialitate")}</span>
                                      {person.inst && <span>• {person.inst}</span>}
                                      {person.city_name && <span>• {person.city_name}</span>}
                                    </div>
                                  </div>
                                </div>

                                {/* Select Indicator */}
                                <div className="shrink-0">
                                  {isSelected ? (
                                    <div className="w-6 h-6 rounded-full bg-[#6366F1] text-white flex items-center justify-center shadow-xs">
                                      <HiOutlineCheck className="w-4 h-4 stroke-[3]" />
                                    </div>
                                  ) : (
                                    <div className="w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-600"></div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="py-10 text-center text-xs text-gray-400">
                            Nu a fost găsită nicio persoană conform căutării.
                          </div>
                        )}
                      </div>

                      {/* Modal Footer Controls */}
                      <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700/60 flex flex-wrap items-center justify-between gap-3">
                        {/* Unlink button if already linked */}
                        <div>
                          {(targetConsent.physician_id || targetConsent.pharmacist_id) && (
                            <button
                              type="button"
                              onClick={() => handleSaveLink(null)}
                              disabled={isSavingLink}
                              className="px-3.5 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl transition-colors inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                            >
                              <HiOutlineTrash className="w-4 h-4" />
                              <span>Elimină asocierea</span>
                            </button>
                          )}
                        </div>

                        <div className="flex items-center gap-2.5 ml-auto">
                          <button
                            type="button"
                            onClick={() => setAssignModalOpen(false)}
                            className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors cursor-pointer"
                          >
                            Anulează
                          </button>

                          <button
                            type="button"
                            onClick={() => handleSaveLink(selectedPersonUniqueId)}
                            disabled={isSavingLink || !selectedPersonUniqueId}
                            className="px-5 py-2 text-xs font-semibold bg-[#6366F1] hover:bg-[#4F46E5] text-white rounded-xl shadow-xs disabled:opacity-50 transition-all inline-flex items-center gap-1.5 cursor-pointer"
                          >
                            {isSavingLink ? (
                              <>
                                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Se asociază...</span>
                              </>
                            ) : (
                              <>
                                <HiOutlineCheck className="w-4 h-4 stroke-[2.5]" />
                                <span>Confirmă asocierea</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Details Modal Dialog */}
      <Transition appear show={detailsOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDetailsOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-3xl bg-white dark:bg-gray-800 p-6 sm:p-7 text-left align-middle shadow-2xl transition-all border border-gray-100 dark:border-gray-700">
                  {selectedRecord && (
                    <div>
                      {/* Modal Header */}
                      <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-700/60 pb-4">
                        <div className="flex items-center gap-3.5">
                          <div className="w-11 h-11 rounded-2xl bg-[#EEF2FF] text-[#6366F1] font-bold text-sm flex items-center justify-center shrink-0">
                            {getInitials(selectedRecord.first_name, selectedRecord.last_name)}
                          </div>
                          <div>
                            <Dialog.Title as="h3" className="text-base font-bold text-[#0F172A] dark:text-gray-100">
                              {selectedRecord.last_name} {selectedRecord.first_name}
                            </Dialog.Title>
                            <p className="text-xs text-[#64748B] dark:text-gray-400">
                              {selectedRecord.specialization || "Fără specializare"} • ID #{selectedRecord.consent_id}
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setDetailsOpen(false)}
                          className="w-8 h-8 rounded-lg inline-flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <HiOutlineXMark className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Modal Body */}
                      <div className="mt-5 space-y-4">
                        {/* Status banner */}
                        <div
                          className={`p-3.5 rounded-xl border flex items-center justify-between ${
                            selectedRecord.marketing_consent === false || selectedRecord.marketing_consent === 0
                              ? "bg-[#FEF2F2] border-red-200 text-[#B91C1C]"
                              : selectedRecord.is_confirmed
                              ? "bg-[#ECFDF5] border-emerald-200 text-[#047857]"
                              : "bg-[#FFF7ED] border-orange-200 text-[#C2410C]"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {selectedRecord.marketing_consent === false || selectedRecord.marketing_consent === 0 ? (
                              <HiOutlineXCircle className="w-5 h-5 shrink-0 text-red-600" />
                            ) : selectedRecord.is_confirmed ? (
                              <HiOutlineCheckCircle className="w-5 h-5 shrink-0" />
                            ) : (
                              <HiOutlineClock className="w-5 h-5 shrink-0" />
                            )}
                            <span className="text-xs font-semibold">
                              Status:{" "}
                              {selectedRecord.marketing_consent === false || selectedRecord.marketing_consent === 0
                                ? "Consimțământ Retras / Dezabonat (Negativ)"
                                : selectedRecord.is_confirmed
                                ? "Consimțământ Activ / Confirmat (Pozitiv)"
                                : "Formular completat – Persoana nu a introdus codul de confirmare primit pe email"}
                            </span>
                          </div>
                          {selectedRecord.confirmed_at && (
                            <span className="text-[11px] font-medium opacity-90 shrink-0">
                              Confirmat la: {dayjs(selectedRecord.confirmed_at).format("DD.MM.YYYY HH:mm")}
                            </span>
                          )}
                        </div>

                        {/* Linked Person Status in Details */}
                        <div className="p-3 rounded-xl border border-indigo-100 bg-indigo-50/50 dark:bg-indigo-950/30 dark:border-indigo-800/40 flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-[#6366F1] text-white flex items-center justify-center shrink-0">
                              <HiOutlineUserPlus className="w-4 h-4" />
                            </div>
                            <div>
                              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">
                                Persoană Asociată în CRM:
                              </span>
                              <span className="text-xs font-bold text-[#0F172A] dark:text-gray-200">
                                {selectedRecord.linked_physician_name ? (
                                  <span className="text-emerald-700 dark:text-emerald-400">
                                    {selectedRecord.linked_physician_name}{" "}
                                    <span className="text-[10px] font-normal opacity-75">
                                      ({selectedRecord.linked_person_type === "pharmacist" || selectedRecord.pharmacist_id ? "Farmacist" : "Medic"})
                                    </span>
                                  </span>
                                ) : (
                                  <span className="text-amber-600 dark:text-amber-400">
                                    Neatribuit (necesită asociere)
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>

                          {selectedRecord.is_confirmed ? (
                            <button
                              type="button"
                              onClick={() => {
                                setDetailsOpen(false);
                                handleOpenAssign(selectedRecord);
                              }}
                              className="px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 text-xs font-semibold text-[#6366F1] border border-indigo-200 shadow-xs hover:bg-indigo-50 cursor-pointer"
                            >
                              {selectedRecord.physician_id || selectedRecord.pharmacist_id ? "Schimbă asocierea" : "Asociază acum"}
                            </button>
                          ) : (
                            <span 
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500 text-xs font-medium border border-gray-200 dark:border-gray-700 cursor-not-allowed"
                              title="Cod neintrodus: Doar acordurile confirmate pot fi asociate."
                            >
                              <HiOutlineLockClosed className="w-3.5 h-3.5" />
                              Neconfirmat prin cod
                            </span>
                          )}
                        </div>

                        {/* Details grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 bg-gray-50/80 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700/40">
                          <div>
                            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                              Organizație / Unitate Medicală
                            </span>
                            <span className="text-xs font-medium text-[#0F172A] dark:text-gray-200">
                              {selectedRecord.medical_unit || "—"}
                            </span>
                          </div>

                          <div>
                            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                              Specializare
                            </span>
                            <span className="text-xs font-medium text-[#0F172A] dark:text-gray-200">
                              {selectedRecord.specialization || "—"}
                            </span>
                          </div>

                          <div>
                            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                              Email
                            </span>
                            <span className="text-xs font-medium text-[#0F172A] dark:text-gray-200">
                              {selectedRecord.email || "—"}
                            </span>
                          </div>

                          <div>
                            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">
                              Telefon
                            </span>
                            <span className="text-xs font-medium text-[#0F172A] dark:text-gray-200">
                              {selectedRecord.phone || "—"}
                            </span>
                          </div>
                        </div>

                        {/* Channels */}
                        <div>
                          <span className="text-xs font-bold text-[#0F172A] dark:text-gray-200 block mb-2">
                            Canale de comunicare acceptate:
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {parseJsonArray(selectedRecord.communication_channels).map((ch) => (
                              <span
                                key={ch}
                                className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-[#F5F3FF] text-[#6366F1] border border-indigo-100 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800/40"
                              >
                                {ch === "email" ? "Email" : ch === "sms" ? "SMS" : ch === "phone" ? "Telefon" : ch}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* All Therapeutic Areas */}
                        <div>
                          <span className="text-xs font-bold text-[#0F172A] dark:text-gray-200 block mb-2">
                            Arii terapeutice selectate:
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {parseJsonArray(selectedRecord.therapeutic_areas).map((a) => (
                              <span
                                key={a}
                                className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                              >
                                {THERAPEUTIC_AREAS_MAP[a] || a}
                              </span>
                            ))}
                            {selectedRecord.other_therapeutic_area && (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                                Altele: {selectedRecord.other_therapeutic_area}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Scanned Document Links in Details */}
                        {getScannedDocuments(selectedRecord).length > 0 && (
                          <div className="p-3.5 rounded-xl border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-800/40 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] font-bold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider block">
                                Document(e) semnat(e) fizic atașat(e):
                              </span>
                              <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                                {getScannedDocuments(selectedRecord).length} {getScannedDocuments(selectedRecord).length === 1 ? "fișier" : "fișiere"}
                              </span>
                            </div>
                            <div className="space-y-2">
                              {getScannedDocuments(selectedRecord).map((doc, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center justify-between p-2.5 rounded-xl bg-white dark:bg-gray-800 border border-emerald-100 dark:border-emerald-800/60 text-xs shadow-xs gap-2"
                                >
                                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                                    <div className="w-8 h-8 rounded-lg bg-[#10B981] text-white flex items-center justify-center shrink-0">
                                      <HiOutlineDocumentText className="w-4.5 h-4.5" />
                                    </div>
                                    <div className="min-w-0">
                                      <span className="font-bold text-[#0F172A] dark:text-gray-200 truncate block">
                                        {doc.name}
                                      </span>
                                      <span className="text-[10px] text-gray-400">
                                        Fișier {idx + 1} din {getScannedDocuments(selectedRecord).length}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => handleOpenDocViewer(selectedRecord, idx)}
                                      className="px-2.5 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/60 text-[11px] font-bold text-[#047857] dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 shadow-xs hover:bg-emerald-100 dark:hover:bg-emerald-900 inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                                    >
                                      <HiOutlineEye className="w-3.5 h-3.5" />
                                      <span>Previzualizează</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDownloadDoc(doc)}
                                      className="px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-700 text-[11px] font-bold text-slate-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 shadow-xs hover:bg-gray-50 dark:hover:bg-gray-600 inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                                      title="Descarcă pe calculator"
                                    >
                                      <HiOutlineArrowDownTray className="w-3.5 h-3.5" />
                                      <span>Descarcă</span>
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Audit Details & Legal Compliance Trail */}
                        <div className="pt-4 border-t border-gray-100 dark:border-gray-700/60 space-y-3.5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <HiOutlineShieldCheck className="w-4 h-4 text-[#6366F1]" />
                              <span className="text-xs font-bold text-[#0F172A] dark:text-gray-100 uppercase tracking-wider">
                                Trasabilitate Audit & Conformitate (Legea RM 195/2024)
                              </span>
                            </div>
                            <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-gray-300">
                              ID #{selectedRecord.consent_id}
                            </span>
                          </div>

                          {/* 3 Status Cards: Înregistrare, Confirmare Cod, Dezabonare */}
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                            {/* Card 1: Înregistrare (Cine a trecut acordul) */}
                            <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-gray-700/40 border border-slate-200/70 dark:border-gray-600/50 text-[11px] space-y-1">
                              <div className="flex items-center gap-1.5 font-bold text-[#0F172A] dark:text-gray-200">
                                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                <span>1. Înregistrare acord</span>
                              </div>
                              <div className="text-gray-600 dark:text-gray-300">
                                <span className="font-semibold text-gray-500">Persoană:</span>{" "}
                                <span className="font-bold">{selectedRecord.first_name} {selectedRecord.last_name}</span>
                              </div>
                              <div className="text-gray-600 dark:text-gray-300">
                                <span className="font-semibold text-gray-500">Data:</span>{" "}
                                {selectedRecord.created_at ? dayjs(selectedRecord.created_at).format("DD.MM.YYYY HH:mm:ss") : "—"}
                              </div>
                              {selectedRecord.registered_ip && (
                                <div className="text-gray-600 dark:text-gray-300 truncate" title={selectedRecord.registered_ip}>
                                  <span className="font-semibold text-gray-500">IP:</span> {selectedRecord.registered_ip}
                                </div>
                              )}
                              {selectedRecord.registered_user_agent && (
                                <div className="text-gray-500 text-[10px] truncate" title={selectedRecord.registered_user_agent}>
                                  {selectedRecord.registered_user_agent}
                                </div>
                              )}
                            </div>

                            {/* Card 2: Confirmare Cod OTP */}
                            <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-gray-700/40 border border-slate-200/70 dark:border-gray-600/50 text-[11px] space-y-1">
                              <div className="flex items-center gap-1.5 font-bold text-[#0F172A] dark:text-gray-200">
                                <span className={`w-2 h-2 rounded-full ${selectedRecord.is_confirmed ? "bg-emerald-500" : "bg-amber-500"}`}></span>
                                <span>2. Confirmare cod OTP</span>
                              </div>
                              <div className="text-gray-600 dark:text-gray-300">
                                <span className="font-semibold text-gray-500">Statut:</span>{" "}
                                <span className={`font-bold ${selectedRecord.is_confirmed ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`}>
                                  {selectedRecord.is_confirmed ? "Confirmat prin cod" : "Cod neintrodus"}
                                </span>
                              </div>
                              <div className="text-gray-600 dark:text-gray-300">
                                <span className="font-semibold text-gray-500">Data:</span>{" "}
                                {selectedRecord.confirmed_at ? dayjs(selectedRecord.confirmed_at).format("DD.MM.YYYY HH:mm:ss") : "—"}
                              </div>
                              {selectedRecord.confirmed_ip && (
                                <div className="text-gray-600 dark:text-gray-300 truncate" title={selectedRecord.confirmed_ip}>
                                  <span className="font-semibold text-gray-500">IP:</span> {selectedRecord.confirmed_ip}
                                </div>
                              )}
                              {selectedRecord.confirmed_user_agent && (
                                <div className="text-gray-500 text-[10px] truncate" title={selectedRecord.confirmed_user_agent}>
                                  {selectedRecord.confirmed_user_agent}
                                </div>
                              )}
                            </div>

                            {/* Card 3: Dezabonare (dacă există) */}
                            <div className={`p-2.5 rounded-xl border text-[11px] space-y-1 ${
                              selectedRecord.marketing_consent === false || selectedRecord.marketing_consent === 0 || selectedRecord.unsubscribed_at
                                ? "bg-red-50/60 dark:bg-red-950/30 border-red-200 dark:border-red-800/40 text-red-900 dark:text-red-300"
                                : "bg-slate-50 dark:bg-gray-700/40 border-slate-200/70 dark:border-gray-600/50 text-gray-500 dark:text-gray-400"
                            }`}>
                              <div className="flex items-center gap-1.5 font-bold">
                                <span className={`w-2 h-2 rounded-full ${
                                  selectedRecord.marketing_consent === false || selectedRecord.marketing_consent === 0 || selectedRecord.unsubscribed_at
                                    ? "bg-red-600"
                                    : "bg-gray-400"
                                }`}></span>
                                <span>3. Dezabonare</span>
                              </div>
                              <div>
                                <span className="font-semibold opacity-80">Statut:</span>{" "}
                                <span className="font-bold">
                                  {selectedRecord.marketing_consent === false || selectedRecord.marketing_consent === 0 || selectedRecord.unsubscribed_at
                                    ? "Dezabonat (Negativ)"
                                    : "Activ (Nedezabonat)"}
                                </span>
                              </div>
                              {(selectedRecord.unsubscribed_at || (selectedRecord.marketing_consent === false && selectedRecord.updated_at)) && (
                                <div>
                                  <span className="font-semibold opacity-80">Data:</span>{" "}
                                  {dayjs(selectedRecord.unsubscribed_at || selectedRecord.updated_at).format("DD.MM.YYYY HH:mm:ss")}
                                </div>
                              )}
                              {selectedRecord.unsubscribed_ip && (
                                <div className="truncate" title={selectedRecord.unsubscribed_ip}>
                                  <span className="font-semibold opacity-80">IP:</span> {selectedRecord.unsubscribed_ip}
                                </div>
                              )}
                              {selectedRecord.unsubscribed_user_agent && (
                                <div className="text-[10px] opacity-75 truncate" title={selectedRecord.unsubscribed_user_agent}>
                                  {selectedRecord.unsubscribed_user_agent}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Jurnal Evenimente Audit Log */}
                          <div className="mt-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[11px] font-bold text-[#0F172A] dark:text-gray-200 flex items-center gap-1.5">
                                <HiOutlineClipboardDocumentList className="w-3.5 h-3.5 text-[#6366F1]" />
                                Jurnal de Evenimente Audit
                              </span>
                              <span className="text-[10px] text-gray-500">
                                {loadingAuditLogs ? "Se încarcă jurnalul..." : `${consentAuditLogs.length} înregistrări în log`}
                              </span>
                            </div>

                            {loadingAuditLogs ? (
                              <div className="py-3 text-center text-xs text-gray-400 flex items-center justify-center gap-2">
                                <HiOutlineArrowPath className="w-3.5 h-3.5 animate-spin text-[#6366F1]" />
                                <span>Se preia istoricul de audit...</span>
                              </div>
                            ) : consentAuditLogs.length > 0 ? (
                              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                {consentAuditLogs.map((log) => {
                                  const isCreate = log.action_type === "CREATE";
                                  const isConfirm = log.action_type === "CONFIRM";
                                  const isUnsub = log.action_type === "UNSUBSCRIBE";
                                  const isReq = log.action_type === "REQUEST";
                                  const badgeBg = isConfirm
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400"
                                    : isUnsub
                                    ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400"
                                    : isReq
                                    ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400"
                                    : isCreate
                                    ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400"
                                    : "bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300";

                                  return (
                                    <div
                                      key={log.id}
                                      className="p-2 rounded-lg bg-gray-50/70 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700/50 text-[11px] space-y-0.5"
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span className={`px-1.5 py-0.2 rounded font-bold text-[9px] border uppercase ${badgeBg}`}>
                                            {log.action_type}
                                          </span>
                                          <span className="font-bold text-[#0F172A] dark:text-gray-200">
                                            {log.user_name || log.user_login || "Public"}
                                          </span>
                                          {log.user_role && (
                                            <span className="text-[10px] text-gray-400 font-medium">
                                              ({log.user_role})
                                            </span>
                                          )}
                                        </div>
                                        <span className="text-[10px] text-gray-400 shrink-0 font-medium">
                                          {dayjs(log.created_at).format("DD.MM.YYYY HH:mm:ss")}
                                        </span>
                                      </div>
                                      <p className="text-[#475569] dark:text-gray-300 text-[11px] leading-tight mt-0.5">
                                        {log.description}
                                      </p>
                                      {log.ip_address && (
                                        <div className="text-[10px] text-gray-400 font-mono">
                                          IP: {log.ip_address}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="py-2 text-center text-xs text-gray-400">
                                Nu există înregistrări separate în log pentru această adresă de email.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Modal Footer */}
                      <div className="mt-6 flex items-center justify-between">
                        {canEdit ? (
                          <button
                            type="button"
                            onClick={() => {
                              setDetailsOpen(false);
                              handleOpenEdit(selectedRecord);
                            }}
                            className="px-4 py-2 text-xs font-bold bg-[#252a32] hover:bg-[#1b1f25] text-white border border-[#252a32] rounded-xl transition-all inline-flex items-center gap-1.5 cursor-pointer shadow-xs hover:scale-105"
                          >
                            <HiOutlinePencilSquare className="w-4 h-4" />
                            <span>Editează datele</span>
                          </button>
                        ) : (
                          <div></div>
                        )}

                        <button
                          type="button"
                          onClick={() => setDetailsOpen(false)}
                          className="px-4 py-2 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 rounded-xl transition-colors cursor-pointer"
                        >
                          Închide
                        </button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* POP-UP MULTI-STEP: ADĂUGARE ACORD PRIN DOCUMENT SCANAT (SEMNĂTURĂ UDĂ) */}
      <Transition appear show={uploadModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleResetUploadModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-3xl bg-white dark:bg-gray-800 p-6 sm:p-7 text-left align-middle shadow-2xl transition-all border border-gray-100 dark:border-gray-700">
                  {/* Modal Header */}
                  <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-700/60 pb-4">
                    <div className="flex items-center gap-3.5">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#6366F1] to-[#4F46E5] text-white flex items-center justify-center shadow-[0_4px_12px_rgba(99,102,241,0.25)] shrink-0">
                        <HiOutlineDocumentArrowUp className="w-6 h-6" />
                      </div>
                      <div>
                        <Dialog.Title as="h3" className="text-lg font-bold text-[#0F172A] dark:text-gray-100">
                          Adăugare Acord prin Document Fizic
                        </Dialog.Title>
                        <p className="text-xs text-[#64748B] dark:text-gray-400 mt-0.5">
                          Înregistrare consimțământ pe bază de document semnat olograf (semnătură pe hârtie).
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleResetUploadModal}
                      className="w-9 h-9 rounded-xl inline-flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    >
                      <HiOutlineXMark className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Multi-step Visual Progress Bar */}
                  <div className="mt-4 flex items-center justify-between border-b border-gray-100 dark:border-gray-700/60 pb-3 text-xs font-semibold">
                    <div className={`flex items-center gap-2 ${uploadStep === 1 ? "text-[#6366F1] font-bold" : "text-emerald-600 dark:text-emerald-400"}`}>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        uploadStep === 1
                          ? "bg-[#6366F1] text-white"
                          : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                      }`}>
                        {uploadStep > 1 ? "✓" : "1"}
                      </span>
                      <span>Pasul 1: Încărcare fișier scanat</span>
                    </div>

                    <div className="text-gray-300 dark:text-gray-600">→</div>

                    <div className={`flex items-center gap-2 ${uploadStep === 2 ? "text-[#6366F1] font-bold" : "text-gray-400"}`}>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        uploadStep === 2 ? "bg-[#6366F1] text-white" : "bg-gray-100 text-gray-400 dark:bg-gray-700"
                      }`}>
                        2
                      </span>
                      <span>Pasul 2: Date formular (identic acord)</span>
                    </div>
                  </div>

                  {/* Error Alert Message */}
                  {docFormError && (
                    <div className="mt-4 p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 text-xs text-rose-700 dark:text-rose-300 flex items-center gap-2">
                      <HiOutlineExclamationCircle className="w-5 h-5 shrink-0" />
                      <span>{docFormError}</span>
                    </div>
                  )}

                  {/* STEP 1: FILE UPLOAD / DRAG & DROP */}
                  {uploadStep === 1 && (
                    <div className="mt-5 space-y-4">
                      <div className="p-3.5 rounded-2xl bg-[#EEF2FF] dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-800/40 flex items-start gap-3">
                        <HiOutlineDocumentText className="w-5 h-5 text-[#6366F1] shrink-0 mt-0.5" />
                        <div className="text-xs text-[#334155] dark:text-indigo-200 leading-relaxed">
                          <span className="font-bold text-[#0F172A] dark:text-gray-100">Semnătură olografă:</span>{" "}
                          Încarcă fișierul scanat (PDF) sau fotografia clară a acordului semnat fizic. Documentul va fi arhivat și accesibil direct din CRM.
                        </div>
                      </div>

                      {/* Transforming Dropzone / Approved Container */}
                      {!docFiles || docFiles.length === 0 ? (
                        <label
                          onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                          }}
                          onDragLeave={() => setIsDragging(false)}
                          onDrop={(e) => {
                            e.preventDefault();
                            setIsDragging(false);
                            const files = e.dataTransfer?.files;
                            if (files && files.length > 0) processSelectedFiles(files);
                          }}
                          className={`group relative overflow-hidden border-2 border-dashed rounded-3xl p-8 sm:p-9 text-center transition-all duration-300 cursor-pointer block ${
                            isDragging
                              ? "border-[#6366F1] bg-[#EEF2FF] dark:bg-indigo-950/50 ring-4 ring-[#6366F1]/20 scale-[1.02] shadow-xl shadow-indigo-500/15"
                              : "border-gray-200 dark:border-gray-700 hover:border-[#6366F1] dark:hover:border-indigo-500 bg-gray-50/50 dark:bg-gray-700/20 hover:bg-[#EEF2FF]/40 hover:scale-[1.01] hover:shadow-lg hover:shadow-indigo-500/5 animate-docPulseBorder"
                          }`}
                        >
                          <input
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-tr from-[#EEF2FF] to-[#E0E7FF] dark:from-indigo-950/80 dark:to-indigo-900/60 text-[#6366F1] dark:text-indigo-300 flex items-center justify-center mb-3.5 shadow-sm transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 animate-docFloat">
                            <HiOutlineDocumentPlus className="w-8 h-8" />
                          </div>
                          <div className="text-sm font-extrabold text-[#0F172A] dark:text-gray-100 group-hover:text-[#6366F1] dark:group-hover:text-indigo-400 transition-colors">
                            Apasă pentru a alege fișierul scanat sau trage-l aici
                          </div>
                          <div className="text-xs text-[#64748B] dark:text-gray-400 mt-1">
                            Formate acceptate: PDF, DOCX, DOC, JPG, PNG (Max. 15MB per fișier)
                          </div>
                        </label>
                      ) : (
                        <div className="animate-docPopIn relative overflow-hidden border-2 border-emerald-500 bg-gradient-to-b from-[#ECFDF5] via-[#F0FDF4] to-[#ECFDF5] dark:from-emerald-950/50 dark:via-emerald-950/30 dark:to-emerald-950/40 ring-4 ring-emerald-500/15 shadow-[0_8px_30px_rgba(16,185,129,0.18)] rounded-3xl p-6 sm:p-7 text-center transition-all duration-300">
                          {/* Approved status pill badge with ping indicator */}
                          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full text-xs font-extrabold bg-[#DCFCE7] text-[#15803D] dark:bg-emerald-900/70 dark:text-emerald-300 border border-emerald-300/80 dark:border-emerald-700/60 shadow-xs mb-3">
                            <span className="relative flex h-2.5 w-2.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-600"></span>
                            </span>
                            <span>
                              {docFiles.length === 1
                                ? "1 DOCUMENT VALIDAT & APROBAT"
                                : `${docFiles.length} DOCUMENTE VALIDATE & APROBATE`}
                            </span>
                          </div>

                          {/* List of uploaded files with individual Delete button */}
                          <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1 my-3 text-left">
                            {docFiles.map((file, idx) => {
                              const ext = file.name.split('.').pop()?.toUpperCase() || "DOC";
                              return (
                                <div
                                  key={`${file.name}-${idx}`}
                                  className="flex items-center justify-between p-3 rounded-2xl bg-white dark:bg-gray-800 border border-emerald-200/90 dark:border-emerald-800 shadow-xs transition-all hover:border-emerald-400"
                                >
                                  <div className="flex items-center gap-3 min-w-0 pr-2">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-[#10B981] to-[#059669] text-white flex items-center justify-center shrink-0 shadow-xs font-bold text-[10px]">
                                      {ext.length > 4 ? ext.slice(0, 4) : ext}
                                    </div>
                                    <div className="min-w-0">
                                      <h5 className="text-xs font-bold text-[#0F172A] dark:text-gray-100 truncate" title={file.name}>
                                        {file.name}
                                      </h5>
                                      <p className="text-[11px] text-[#047857] dark:text-emerald-400 font-medium">
                                        {(file.size / 1024).toFixed(1)} KB • Pregătit pentru salvare
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveFile(idx)}
                                    className="h-8 px-2.5 rounded-lg bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 border border-rose-200/80 dark:border-rose-900/50 text-xs font-bold inline-flex items-center gap-1.5 transition-all cursor-pointer hover:scale-105 active:scale-95 shrink-0"
                                    title="Elimină fișierul"
                                  >
                                    <HiOutlineTrash className="w-3.5 h-3.5" />
                                    <span>Elimină</span>
                                  </button>
                                </div>
                              );
                            })}
                          </div>

                          {/* Actions within the box: Mai adaugă un fișier & Elimină toate */}
                          <div className="mt-4 pt-3 border-t border-emerald-200/60 dark:border-emerald-800/40 flex flex-wrap items-center justify-center gap-2.5">
                            <label className="h-9 px-3.5 rounded-xl bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 text-xs font-bold text-[#4F46E5] dark:text-indigo-300 inline-flex items-center gap-1.5 shadow-xs cursor-pointer transition-all hover:scale-105 active:scale-95">
                              <HiOutlineDocumentPlus className="w-4 h-4 text-[#6366F1]" />
                              <span>+ Mai adaugă un fișier</span>
                              <input
                                type="file"
                                multiple
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                                onChange={handleFileChange}
                                className="hidden"
                              />
                            </label>

                            <button
                              type="button"
                              onClick={() => setDocFiles([])}
                              className="h-9 px-3.5 rounded-xl bg-white dark:bg-gray-800 hover:bg-rose-50 dark:hover:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 text-xs font-bold text-rose-600 dark:text-rose-400 inline-flex items-center gap-1.5 shadow-xs cursor-pointer transition-all hover:scale-105 active:scale-95"
                            >
                              <HiOutlineTrash className="w-3.5 h-3.5" />
                              <span>Elimină toate</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Step 1 Footer */}
                      <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700/60 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={handleResetUploadModal}
                          className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors cursor-pointer"
                        >
                          Anulează
                        </button>

                        <button
                          type="button"
                          onClick={() => setUploadStep(2)}
                          className="px-5 py-2.5 text-xs font-bold bg-[#6366F1] hover:bg-[#4F46E5] text-white rounded-xl shadow-xs transition-all inline-flex items-center gap-1.5 cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                        >
                          <span>Continuă la Pasul 2 (Date Formular)</span>
                          <span>→</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* STEP 2: MANUAL FORM ENTRY (MATCHING https://crm.dita.md/marketing-consent) */}
                  {uploadStep === 2 && (
                    <div className="mt-5 space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                      {/* Attached file summary pill */}
                      {docFiles.length > 0 && (
                        <div className="px-3.5 py-2 rounded-xl bg-slate-50 dark:bg-gray-700/40 border border-slate-200/80 dark:border-gray-700 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2 min-w-0 pr-2">
                            <HiOutlineDocumentText className="w-4 h-4 text-emerald-600 shrink-0" />
                            <span className="font-semibold text-slate-700 dark:text-gray-200 truncate">
                              {docFiles.length === 1
                                ? `Document atașat: ${docFiles[0].name}`
                                : `${docFiles.length} documente atașate (${docFiles.map((f) => f.name).join(", ")})`}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setUploadStep(1)}
                            className="text-[11px] font-semibold text-[#6366F1] hover:underline shrink-0"
                          >
                            Modifică fișierele
                          </button>
                        </div>
                      )}

                      {/* Row 1: Nume & Prenume */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div>
                          <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-1">
                            Nume <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="lastName"
                            value={docFormData.lastName}
                            onChange={handleDocFieldChange}
                            placeholder="ex: Tabuica"
                            className="w-full h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#6366F1]"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-1">
                            Prenume <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="firstName"
                            value={docFormData.firstName}
                            onChange={handleDocFieldChange}
                            placeholder="ex: Oleg"
                            className="w-full h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#6366F1]"
                          />
                        </div>
                      </div>

                      {/* Row 2: Specializare & Unitate Medicală */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div>
                          <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-1">
                            Specializare medicală <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="specialization"
                            list="specialities-list-doc"
                            value={docFormData.specialization}
                            onChange={handleDocFieldChange}
                            placeholder="ex: Dermatovenerolog, Cardiologie, Farmacist..."
                            className="w-full h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#6366F1]"
                          />
                          <datalist id="specialities-list-doc">
                            {specialitiesList.map((s) => (
                              <option key={s.speciality_id} value={s.description} />
                            ))}
                            <option value="Farmacist" />
                            <option value="Consultant" />
                          </datalist>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-1">
                            Unitate medicală / Farmacie afiliată <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="medicalUnit"
                            value={docFormData.medicalUnit}
                            onChange={handleDocFieldChange}
                            placeholder="ex: IMSP DMDV Chisinau sau Farmacia Familiei"
                            className="w-full h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#6366F1]"
                          />
                        </div>
                      </div>

                      {/* Row 3: Email & Telefon */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div>
                          <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-1">
                            E-mail <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={docFormData.email}
                            onChange={handleDocFieldChange}
                            placeholder="ex: medic@email.com"
                            className="w-full h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#6366F1]"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-1">
                            Telefon
                          </label>
                          <input
                            type="text"
                            name="phone"
                            value={docFormData.phone}
                            onChange={handleDocFieldChange}
                            placeholder="ex: 069123456"
                            className="w-full h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#6366F1]"
                          />
                        </div>
                      </div>

                      {/* Row 4: Canale de comunicare (Document, Email, SMS, Telefon) */}
                      <div>
                        <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-2">
                          Canale de comunicare aprobate:
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {[
                            { id: "document", label: "Document fizic", icon: HiOutlineDocumentText, color: "emerald" },
                            { id: "email", label: "E-mail", icon: HiOutlineEnvelope, color: "blue" },
                            { id: "sms", label: "SMS", icon: HiOutlineChatBubbleLeftRight, color: "amber" },
                            { id: "phone", label: "Telefon (Apel)", icon: HiOutlinePhone, color: "purple" },
                          ].map((ch) => {
                            const isChecked = docFormData.communicationChannels.includes(ch.id);
                            return (
                              <button
                                key={ch.id}
                                type="button"
                                onClick={() => toggleDocChannel(ch.id)}
                                className={`p-2.5 rounded-xl border text-xs font-semibold flex items-center justify-between gap-1.5 transition-all cursor-pointer ${
                                  isChecked
                                    ? "bg-[#EEF2FF] border-[#6366F1] text-[#4F46E5] dark:bg-indigo-950/60 dark:border-indigo-600 dark:text-indigo-300"
                                    : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                <span>{ch.label}</span>
                                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                                  isChecked ? "bg-[#6366F1] text-white" : "border border-gray-300 dark:border-gray-600"
                                }`}>
                                  {isChecked && "✓"}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Row 5: Arii terapeutice */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-bold text-[#0F172A] dark:text-gray-200">
                            Arii terapeutice de interes <span className="text-rose-500">*</span>
                          </label>
                          <button
                            type="button"
                            onClick={toggleDocAllTherapeuticAreas}
                            className="text-[11px] font-bold text-[#6366F1] hover:text-[#4F46E5] cursor-pointer"
                          >
                            {Object.keys(THERAPEUTIC_AREAS_MAP).filter((k) => k !== "other").every((k) => docFormData.therapeuticAreas.includes(k))
                              ? "Deselectează toate"
                              : "Selectează toate"}
                          </button>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-[160px] overflow-y-auto p-2 rounded-xl bg-gray-50/70 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700/50">
                          {Object.entries(THERAPEUTIC_AREAS_MAP).map(([key, label]) => {
                            const isSelected = docFormData.therapeuticAreas.includes(key);
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => toggleDocTherapeuticArea(key)}
                                className={`px-2.5 py-1.5 rounded-lg text-left text-xs font-medium transition-colors flex items-center justify-between ${
                                  isSelected
                                    ? "bg-[#6366F1] text-white"
                                    : "bg-white dark:bg-gray-800 text-[#334155] dark:text-gray-200 border border-gray-200/80 dark:border-gray-700 hover:bg-gray-100"
                                }`}
                              >
                                <span className="truncate">{label}</span>
                                {isSelected && <span className="text-[10px] ml-1">✓</span>}
                              </button>
                            );
                          })}
                        </div>

                        {docFormData.therapeuticAreas.includes("other") && (
                          <div className="mt-2">
                            <input
                              type="text"
                              name="otherTherapeuticArea"
                              value={docFormData.otherTherapeuticArea}
                              onChange={handleDocFieldChange}
                              placeholder="Specificați celelalte arii terapeutice..."
                              className="w-full h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs text-[#0F172A] dark:text-gray-100 focus:outline-none focus:border-[#6366F1]"
                            />
                          </div>
                        )}
                      </div>

                      {/* Row 6: Consent checkbox (Direct confirmation) */}
                      <div className="p-3.5 rounded-xl bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/40">
                        <label className="flex items-start gap-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            name="marketingConsent"
                            checked={docFormData.marketingConsent}
                            onChange={handleDocFieldChange}
                            className="mt-0.5 rounded text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className="text-xs text-[#0F172A] dark:text-gray-200 font-medium leading-relaxed">
                            Confirm că acordul a fost semnat olograf de către persoana menționată mai sus și că datele introduse corespund întocmai cu documentul fizic. Statusul va fi setat direct pe <strong>Confirmat</strong>.
                          </span>
                        </label>
                      </div>

                      {/* Modal Actions */}
                      <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700/60 flex items-center justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => setUploadStep(1)}
                          disabled={isSubmittingDoc}
                          className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors cursor-pointer"
                        >
                          ← Înapoi la fișier
                        </button>

                        <button
                          type="button"
                          onClick={handleSubmitManualDocument}
                          disabled={isSubmittingDoc}
                          className="px-5 py-2.5 text-xs font-bold bg-[#10B981] hover:bg-[#059669] text-white rounded-xl shadow-xs transition-all inline-flex items-center gap-2 cursor-pointer disabled:opacity-50"
                        >
                          {isSubmittingDoc ? (
                            <>
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Se salvează acordul...</span>
                            </>
                          ) : (
                            <>
                              <HiOutlineCheck className="w-4 h-4 stroke-[3]" />
                              <span>Salvează și Confirmă Acordul</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Modal 3.5: POP-UP EDITARE / CORECTARE DATE ACORD */}
      <Transition appear show={editModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setEditModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-3xl bg-white dark:bg-gray-800 p-6 sm:p-7 text-left align-middle shadow-2xl transition-all border border-gray-100 dark:border-gray-700">
                  {/* Modal Header */}
                  <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-700/60 pb-4">
                    <div className="flex items-center gap-3.5">
                      <div className="w-12 h-12 rounded-2xl bg-[#252a32] text-white flex items-center justify-center shadow-[0_4px_12px_rgba(37,42,50,0.25)] shrink-0">
                        <HiOutlinePencilSquare className="w-6 h-6" />
                      </div>
                      <div>
                        <Dialog.Title as="h3" className="text-lg font-bold text-[#0F172A] dark:text-gray-100">
                          Editare Date Acord de Marketing
                        </Dialog.Title>
                        <p className="text-xs text-[#64748B] dark:text-gray-400 mt-0.5">
                          Corectează datele de contact, numele sau ariile terapeutice ale acordului #{editingRecord?.consent_id}.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setEditModalOpen(false)}
                      className="w-9 h-9 rounded-xl inline-flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    >
                      <HiOutlineXMark className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Error Alert Message */}
                  {editFormError && (
                    <div className="mt-4 p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 text-xs text-rose-700 dark:text-rose-300 flex items-center gap-2">
                      <HiOutlineExclamationCircle className="w-5 h-5 shrink-0" />
                      <span>{editFormError}</span>
                    </div>
                  )}

                  {/* Form Body */}
                  <div className="mt-5 space-y-4 max-h-[62vh] overflow-y-auto pr-1">
                    {/* Row 1: Nume & Prenume */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div>
                        <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-1">
                          Nume de familie <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={editFormData.lastName}
                          onChange={handleEditFieldChange}
                          placeholder="ex: Arcus"
                          className="w-full h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#252a32] focus:ring-2 focus:ring-[#252a32]/15"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-1">
                          Prenume <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={editFormData.firstName}
                          onChange={handleEditFieldChange}
                          placeholder="ex: Inga"
                          className="w-full h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#252a32] focus:ring-2 focus:ring-[#252a32]/15"
                        />
                      </div>
                    </div>

                    {/* Row 2: Specializare & Unitate Medicală */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div>
                        <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-1">
                          Specializare medicală <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="specialization"
                          list="specialities-list-edit"
                          value={editFormData.specialization}
                          onChange={handleEditFieldChange}
                          placeholder="ex: Farmacist, Dermatovenerolog, Cardiologie..."
                          className="w-full h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#252a32] focus:ring-2 focus:ring-[#252a32]/15"
                        />
                        <datalist id="specialities-list-edit">
                          {specialitiesList.map((s) => (
                            <option key={s.speciality_id} value={s.description} />
                          ))}
                          <option value="Farmacist" />
                          <option value="Consultant" />
                        </datalist>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-1">
                          Unitate medicală / Farmacie afiliată <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="medicalUnit"
                          value={editFormData.medicalUnit}
                          onChange={handleEditFieldChange}
                          placeholder="ex: FF Cahul sau Spitalul Raional"
                          className="w-full h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#252a32] focus:ring-2 focus:ring-[#252a32]/15"
                        />
                      </div>
                    </div>

                    {/* Row 3: Email & Telefon */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div>
                        <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-1">
                          E-mail <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={editFormData.email}
                          onChange={handleEditFieldChange}
                          placeholder="ex: farmacie@email.com"
                          className="w-full h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#252a32] focus:ring-2 focus:ring-[#252a32]/15"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-1">
                          Telefon
                        </label>
                        <input
                          type="text"
                          name="phone"
                          value={editFormData.phone}
                          onChange={handleEditFieldChange}
                          placeholder="ex: 069614453"
                          className="w-full h-10 px-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/50 text-xs sm:text-sm text-[#0F172A] dark:text-gray-100 placeholder-[#94A3B8] shadow-xs focus:outline-none focus:border-[#252a32] focus:ring-2 focus:ring-[#252a32]/15"
                        />
                      </div>
                    </div>

                    {/* Row 4: Canale de comunicare */}
                    <div>
                      <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-2">
                        Canale de comunicare aprobate:
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                          { id: "document", label: "Document fizic", icon: HiOutlineDocumentText },
                          { id: "email", label: "E-mail", icon: HiOutlineEnvelope },
                          { id: "sms", label: "SMS", icon: HiOutlineChatBubbleLeftRight },
                          { id: "phone", label: "Telefon (Apel)", icon: HiOutlinePhone },
                        ].map((ch) => {
                          const isChecked = editFormData.communicationChannels.includes(ch.id);
                          return (
                            <button
                              key={ch.id}
                              type="button"
                              onClick={() => toggleEditChannel(ch.id)}
                              className={`p-2.5 rounded-xl border text-xs font-semibold flex items-center justify-between gap-1.5 transition-all cursor-pointer ${
                                isChecked
                                  ? "bg-[#252a32] border-[#252a32] text-white shadow-xs"
                                  : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              <span>{ch.label}</span>
                              <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                                isChecked ? "bg-white/20 text-white font-bold" : "border border-gray-300 dark:border-gray-600"
                              }`}>
                                {isChecked && "✓"}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Row 5: Arii terapeutice */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-bold text-[#0F172A] dark:text-gray-200">
                          Arii terapeutice de interes <span className="text-rose-500">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={toggleEditAllTherapeuticAreas}
                          className="text-[11px] font-bold text-[#252a32] hover:text-black dark:text-gray-200 cursor-pointer"
                        >
                          {Object.keys(THERAPEUTIC_AREAS_MAP).filter((k) => k !== "other").every((k) => editFormData.therapeuticAreas.includes(k))
                            ? "Deselectează toate"
                            : "Selectează toate"}
                        </button>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-[160px] overflow-y-auto p-2 rounded-xl bg-gray-50/70 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700/50">
                        {Object.entries(THERAPEUTIC_AREAS_MAP).map(([key, label]) => {
                          const isSelected = editFormData.therapeuticAreas.includes(key);
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => toggleEditTherapeuticArea(key)}
                              className={`px-2.5 py-1.5 rounded-lg text-left text-xs font-medium transition-colors flex items-center justify-between ${
                                isSelected
                                  ? "bg-[#252a32] text-white font-bold shadow-2xs"
                                  : "bg-white dark:bg-gray-800 text-[#334155] dark:text-gray-200 border border-gray-200/80 dark:border-gray-700 hover:bg-gray-100"
                              }`}
                            >
                              <span className="truncate">{label}</span>
                              {isSelected && <span className="text-[10px] ml-1">✓</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Row 6: Documente atașate */}
                    <div>
                      <label className="block text-xs font-bold text-[#0F172A] dark:text-gray-200 mb-2">
                        Fișiere atașate (Document acord):
                      </label>
                      <div className="space-y-2">
                        {editDocFiles.map((file, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 dark:bg-gray-700/40 border border-slate-200 dark:border-gray-700 text-xs"
                          >
                            <div className="flex items-center gap-2 truncate">
                              <HiOutlineDocumentText className="w-4 h-4 text-emerald-600 shrink-0" />
                              <span className="font-semibold text-slate-700 dark:text-gray-200 truncate">{file.name}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveEditFile(idx)}
                              className="text-rose-600 hover:text-rose-700 text-xs font-bold px-2 py-1 rounded hover:bg-rose-50 dark:hover:bg-rose-950/40 cursor-pointer"
                            >
                              Șterge
                            </button>
                          </div>
                        ))}

                        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-xs font-semibold text-[#252a32] dark:text-gray-200 hover:bg-gray-100/60 dark:hover:bg-gray-700/60 cursor-pointer">
                          <HiOutlineDocumentPlus className="w-4 h-4" />
                          <span>+ Adaugă document nou</span>
                          <input
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                            onChange={handleEditFileChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700/60 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setEditModalOpen(false)}
                      className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors cursor-pointer"
                    >
                      Anulează
                    </button>

                    <button
                      type="button"
                      onClick={handleSubmitEdit}
                      disabled={isSubmittingEdit}
                      className="px-5 py-2.5 text-xs font-bold bg-[#252a32] hover:bg-[#1b1f25] disabled:opacity-50 text-white rounded-xl shadow-xs transition-all inline-flex items-center gap-1.5 cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                    >
                      {isSubmittingEdit ? (
                        <>
                          <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                          <span>Se salvează...</span>
                        </>
                      ) : (
                        <>
                          <HiOutlineCheck className="w-4 h-4 stroke-[2.5]" />
                          <span>Salvează Modificările</span>
                        </>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Modal 4: Scanned Document Viewer & Reader Modal */}
      <Transition appear show={docViewerOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDocViewerOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-3 sm:p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95 translate-y-2"
                enterTo="opacity-100 scale-100 translate-y-0"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100 translate-y-0"
                leaveTo="opacity-0 scale-95 translate-y-2"
              >
                <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-3xl bg-white dark:bg-gray-800 p-4 sm:p-6 text-left align-middle shadow-2xl transition-all border border-gray-100 dark:border-gray-700 flex flex-col max-h-[92vh]">
                  {(() => {
                    const docs = getScannedDocuments(docViewerRecord);
                    const activeDoc = docs[activeDocIndex] || docs[0] || null;
                    if (!activeDoc) {
                      return (
                        <div className="py-12 text-center text-gray-500">
                          Niciun document disponibil.
                        </div>
                      );
                    }

                    const docUrl = getDocumentUrl(activeDoc, false);
                    const downloadUrl = getDocumentUrl(activeDoc, true);
                    const fileNameLower = (activeDoc.name || activeDoc.path || "").toLowerCase();
                    const isPdf = fileNameLower.endsWith(".pdf");
                    const isImage = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".bmp"].some((ext) =>
                      fileNameLower.endsWith(ext)
                    );
                    const isWord = [".doc", ".docx"].some((ext) => fileNameLower.endsWith(ext));

                    return (
                      <>
                        {/* Top Bar / Header */}
                        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/60 pb-4 gap-3 shrink-0">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-2xl bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-xs shadow-emerald-500/20">
                              <HiOutlineDocumentText className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                              <Dialog.Title
                                as="h3"
                                className="text-base sm:text-lg font-extrabold text-[#0F172A] dark:text-gray-100 truncate"
                              >
                                {activeDoc.name || "Document Acord"}
                              </Dialog.Title>
                              <p className="text-xs text-[#64748B] dark:text-gray-400 truncate mt-0.5">
                                {docViewerRecord?.last_name} {docViewerRecord?.first_name} • {docViewerRecord?.specialization || "Medic / Farmacist"} • {docViewerRecord?.medical_unit || "CRM"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => handleDownloadDoc(activeDoc)}
                              className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold inline-flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                              title="Descarcă documentul pe calculator"
                            >
                              <HiOutlineArrowDownTray className="w-4 h-4" />
                              <span className="hidden sm:inline">Descarcă</span>
                            </button>

                            <a
                              href={docUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-[#334155] dark:text-gray-200 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                              title="Deschide documentul într-o filă nouă de browser"
                            >
                              <HiOutlineArrowTopRightOnSquare className="w-4 h-4" />
                              <span className="hidden md:inline">Tab nou</span>
                            </a>

                            <button
                              type="button"
                              onClick={() => setDocViewerOpen(false)}
                              className="w-9 h-9 rounded-xl inline-flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                            >
                              <HiOutlineXMark className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        {/* Multi-document Tabs (if more than 1 document) */}
                        {docs.length > 1 && (
                          <div className="flex items-center gap-2 pt-3 pb-1 border-b border-gray-100 dark:border-gray-700/60 overflow-x-auto shrink-0">
                            {docs.map((d, dIdx) => (
                              <button
                                key={dIdx}
                                type="button"
                                onClick={() => setActiveDocIndex(dIdx)}
                                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1.5 shrink-0 ${
                                  activeDocIndex === dIdx
                                    ? "bg-emerald-500 text-white shadow-xs"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                                }`}
                              >
                                <HiOutlineDocumentText className="w-3.5 h-3.5" />
                                <span className="truncate max-w-[150px]">{d.name || `Document ${dIdx + 1}`}</span>
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Document Reader Main Display */}
                        <div className="mt-4 flex-1 min-h-[350px] max-h-[72vh] overflow-y-auto rounded-2xl bg-[#F8FAFC] dark:bg-gray-900 border border-gray-200/80 dark:border-gray-700 flex items-center justify-center p-2 relative">
                          {isPdf ? (
                            <iframe
                              src={docUrl}
                              title={activeDoc.name}
                              className="w-full h-[68vh] rounded-xl border-0 bg-white"
                            />
                          ) : isImage ? (
                            <div className="w-full h-full flex flex-col items-center justify-center p-2 overflow-auto">
                              <img
                                src={docUrl}
                                alt={activeDoc.name}
                                className="max-h-[66vh] w-auto object-contain rounded-xl shadow-md border border-gray-200 dark:border-gray-700 bg-white"
                              />
                            </div>
                          ) : isWord ? (
                            <div className="text-center p-8 max-w-md">
                              <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto mb-4 border border-blue-200 dark:border-blue-800">
                                <HiOutlineDocumentText className="w-8 h-8" />
                              </div>
                              <h4 className="text-base font-bold text-[#0F172A] dark:text-gray-100">
                                {activeDoc.name}
                              </h4>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-5">
                                Fișier document Word (.docx / .doc). Pentru a vizualiza conținutul complet cu formatarea originală, descărcați fișierul.
                              </p>
                              <a
                                href={downloadUrl}
                                download={activeDoc.name}
                                className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold inline-flex items-center gap-2 shadow-md shadow-blue-500/20 transition-all cursor-pointer"
                              >
                                <HiOutlineArrowDownTray className="w-4 h-4" />
                                <span>Descarcă documentul Word</span>
                              </a>
                            </div>
                          ) : (
                            <div className="text-center p-8 max-w-md">
                              <div className="w-16 h-16 rounded-2xl bg-slate-100 text-slate-600 flex items-center justify-center mx-auto mb-4">
                                <HiOutlineDocumentText className="w-8 h-8" />
                              </div>
                              <h4 className="text-base font-bold text-[#0F172A] dark:text-gray-100">
                                {activeDoc.name}
                              </h4>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-5">
                                Formatul fișierului poate fi vizualizat prin descărcare directă.
                              </p>
                              <a
                                href={downloadUrl}
                                download={activeDoc.name}
                                className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold inline-flex items-center gap-2 shadow-md transition-all cursor-pointer"
                              >
                                <HiOutlineArrowDownTray className="w-4 h-4" />
                                <span>Descarcă fișierul</span>
                              </a>
                            </div>
                          )}
                        </div>

                        {/* Footer status strip */}
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/60 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 shrink-0 px-1">
                          <div>
                            <span>Status: </span>
                            <strong className="text-emerald-600 font-bold">Acord confirmat fizic</strong>
                            {docViewerRecord?.created_at && (
                              <span> • Înregistrat la: {dayjs(docViewerRecord.created_at).format("DD.MM.YYYY HH:mm")}</span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => setDocViewerOpen(false)}
                            className="text-xs font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 cursor-pointer"
                          >
                            Închide
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
